import { useState, useEffect } from 'react';
import { useNavigate, useParams} from 'react-router-dom';
import { useTabParam } from '../hooks/useTabParam';
import { okToLeave } from '../lib/unsavedChanges';
import { getCurrentUser }       from '../lib/auth';
import { CANONICAL_SERVICES, fetchCanonicalServices, fetchAllProfilesWithServices, buildServiceReviewRows, sameServiceName } from '../lib/services';
import { supabase }            from '../lib/supabase';
import { fetchAllRows, fetchAllRowsResult } from '../lib/fetchAll';
import AdminGuard              from '../admin/components/AdminGuard';
import AdminSidebar            from '../admin/components/AdminSidebar';
import SoonScreen              from '../admin/components/SoonScreen';
import HealthSection            from '../admin/sections/HealthSection';
import CravingsScreen           from '../admin/sections/CravingsScreen';
import { findNavItem, panelTotal, failedParts, sectionCount, NARROW_PX, DOCK, MENU_ID } from '../admin/lib/adminNav';
import { loadDuplicatePairs } from '../admin/lib/adminCounts';
import { logAudit }            from '../admin/lib/auditLog';
import { requestBookReembed } from '../lib/reembedBook';

// Existing sections
import OverviewSection    from '../admin/sections/OverviewSection';
import TropeQueueSection  from '../admin/sections/TropeQueueSection';
import SubgenreQueueSection from '../admin/sections/SubgenreQueueSection';
import BooksSection       from '../admin/sections/BooksSection';
import EventsSection      from '../admin/sections/EventsSection';
import AdminUsersSection  from '../admin/sections/UsersSection';
import ReportsSection     from '../admin/sections/ReportsSection';
import CsvSection         from '../admin/sections/CsvSection';
import AnalyticsSection   from '../admin/sections/AnalyticsSection';
import AuditLogSection    from '../admin/sections/AuditLogSection';
import JournalPromptsSection from '../admin/sections/JournalPromptsSection';
import JournalSection from '../admin/sections/JournalSection';
import SettingsSection    from '../admin/sections/SettingsSection';
import AffiliateSection   from '../admin/sections/AffiliateSection';
import FeedbackSection    from '../admin/sections/FeedbackSection';
import AdminEventImporter from '../admin/sections/AdminEventImporter';
import AdminAuthorSection from '../admin/sections/AdminAuthorSection';
import { resolveTagWithBucket, tagNameError } from '../lib/tropeUtils';
import AdminPipelinesSection from '../admin/sections/AdminPipelinesSection';
import { onCoverError } from '../lib/coverFallback';

// A genre belongs in book_genres / book_subgenres since PR 5, not in book_tags,
// and nothing reads book_tags for genre any more. Same refusal the Books tag
// editor gives (BooksSection.jsx, addTag), so the two screens agree.
const genreRefusal = (name) =>
  `Tag NOT added. "${name}" is a genre, and genres are no longer stored as tags. ` +
  `Add genres from the Books screen once its genre picker lands.`;

// resolveTagWithBucket answers source 'default' when it found the tag but could
// not read what kind of tag it is (unmapped category, failed read, or a
// near-match whose own bucket is unreadable). It then guesses 'trope'. The
// Books tag editor refuses those cases, so the queue does too.
const unknownKindRefusal = (name) =>
  `Tag NOT added: couldn't tell what kind of tag "${name}" is. Check its category in the trope catalogue.`;

// A comp with no author is stored as pop_book_author NULL, and ILIKE '' never
// matches NULL, so that case needs IS NULL (same as the author app's sync).
const withPopAuthor = (query, author, esc) =>
  author ? query.ilike('pop_book_author', esc(author)) : query.is('pop_book_author', null);

// ── Approval Queue ────────────────────────────────────────────────────────────
function ApprovalQueueSection({ onBadgeChange, isMobile }) {
const [tab,       setTab]       = useTabParam('tab', 'books');
  // Books
  const [held,      setHeld]      = useState([]);
  const [published, setPublished] = useState([]);
  const [loadingB,  setLoadingB]  = useState(true);
  const [editingId,    setEditingId]    = useState(null);
  const [newTag,       setNewTag]       = useState('');
  const [newDescription, setNewDescription] = useState('');
  const [editPubBook,  setEditPubBook]  = useState(null); // published book tag editor
  const [pubTags,      setPubTags]      = useState([]);
  const [pubTagInput,  setPubTagInput]  = useState('');
  const [savingPubTag, setSavingPubTag] = useState(false);
  // Tropes
  // Services
  const [svcReview,    setSvcReview]    = useState([]);
  const [loadingSvc,   setLoadingSvc]   = useState(false);
  const [suggestions,  setSuggestions]  = useState([]);   // NEW — process-service suggestions
  const [newCanonical,   setNewCanonical]   = useState('');
  const [canonicalList,  setCanonicalList]  = useState(CANONICAL_SERVICES);
  // Toast
  const [toast,     setToast]     = useState(null);
  const ping = msg => { setToast(msg); setTimeout(() => setToast(null), 2000); };

  useEffect(() => {
    // Load merged canonical list (hardcoded + DB additions)
    fetchCanonicalServices().then(setCanonicalList);
  }, []);

  useEffect(() => {
    if (tab === 'books' && loadingB) loadBooks();
    if (tab === 'services' && !loadingSvc && svcReview.length === 0) loadServices();
  }, [tab]);

  async function loadServices() {
    setLoadingSvc(true);
    const profiles = await fetchAllProfilesWithServices();
    const rows = buildServiceReviewRows(profiles);
    setSvcReview(rows);

    // Paged: "Suggested matches · N" prints this array's length.
    const sugg = await fetchAllRows(() => supabase
      .from('service_merge_log')
      .select('id, user_id, username, original_name, normalized_name, matched_service_name, similarity_score, method, created_at')
      .eq('status', 'pending'));
    sugg.sort((a, b) => (b.similarity_score || 0) - (a.similarity_score || 0));
    setSuggestions(sugg || []);

    setLoadingSvc(false);
  }

  async function dismissService(userId, serviceName) {
    const { data: profile, error: fetchErr } = await supabase
      .from('user_profiles').select('ask_me_about').eq('user_id', userId).single();
    if (fetchErr || !profile) { ping('Failed to load profile'); return; }
    const services = (profile.ask_me_about.services || []).map(s =>
      sameServiceName(s.name, serviceName) ? { ...s, needs_review: false } : s
    );
    const { error } = await supabase.from('user_profiles')
      .update({ ask_me_about: { ...profile.ask_me_about, services } })
      .eq('user_id', userId);
    if (error) { ping('Update failed: ' + error.message); return; }
    // Matched case-insensitively, the same way the write above does. The
    // update at :114 clears needs_review on every casing the reader typed, so
    // removing only the exactly-matching row left the other casing on screen
    // as a queue entry that had already been dismissed in the database —
    // clicking it again did nothing visible. :147 already did this correctly.
    setSvcReview(prev => prev.filter(r => !(r.user_id === userId && sameServiceName(r.service, serviceName))));
    ping('Dismissed');
  }

  async function mapServiceToCanonical(serviceName, canonicalName) {
    const profiles = await fetchAllProfilesWithServices();
    let updated = 0;
    const successfulIds = [];
    for (const p of profiles) {
      const match = (p.ask_me_about?.services || []).find(s => sameServiceName(s.name, serviceName));
      if (!match) continue;
      const services = p.ask_me_about.services.map(s =>
        sameServiceName(s.name, serviceName) ? { ...s, normalized_name: canonicalName, needs_review: false } : s
      );
      const { error } = await supabase.from('user_profiles')
        .update({ ask_me_about: { ...p.ask_me_about, services } })
        .eq('user_id', p.user_id);
      if (!error) { updated++; successfulIds.push(p.user_id); }
    }
    // Only clear rows where writes succeeded. Matched case-insensitively so the
    // reader who typed "cosplayer" clears alongside the one who typed
    // "Cosplayer" — the write above already updated both.
    setSvcReview(prev => prev.filter(r => !(sameServiceName(r.service, serviceName) && successfulIds.includes(r.user_id))));
    if (updated === 0) { ping('No updates succeeded, check permissions'); return; }
    ping(`Mapped "${serviceName}" → "${canonicalName}" for ${updated} reader${updated !== 1 ? 's' : ''}`);
  }

  // NEW — approving applies to every profile carrying that exact typed
  // string (mapServiceToCanonical already does this bulk lookup), and
  // clears every OTHER pending suggestion sharing the same normalized
  // string, so two readers typing "Cosplaying" don't leave a stale second
  // queue entry after you've approved the first.
  async function approveSuggestion(sugg) {
    await mapServiceToCanonical(sugg.original_name, sugg.matched_service_name);
    const user = await getCurrentUser();
    await supabase.from('service_merge_log')
      .update({ status: 'approved', reviewed_by: user?.id || null, reviewed_at: new Date().toISOString() })
      .eq('normalized_name', sugg.normalized_name)
      .eq('status', 'pending');
    setSuggestions(prev => prev.filter(s => s.normalized_name !== sugg.normalized_name));
    ping(`Approved "${sugg.original_name}" → "${sugg.matched_service_name}"`);
  }

  // NEW
 async function rejectSuggestion(sugg) {
    const user = await getCurrentUser();
    await supabase.from('service_merge_log')
      .update({ status: 'rejected', reviewed_by: user?.id || null, reviewed_at: new Date().toISOString() })
      .eq('id', sugg.id);
    setSuggestions(prev => prev.filter(s => s.id !== sugg.id));
    ping('Rejected, kept as custom');
  }

  async function addCanonicalService(name) {
    if (!name.trim()) return;
    // Persist to DB so it survives reload and reaches reader dropdown
    const { error } = await supabase
      .from('canonical_services')
      .upsert({ name: name.trim() }, { onConflict: 'name', ignoreDuplicates: true });
    if (error) { ping('Failed to add canonical: ' + error.message); return; }
    await mapServiceToCanonical(name.trim(), name.trim());
    // Refresh merged canonical list so dropdown shows the new entry immediately
    fetchCanonicalServices().then(setCanonicalList);
    setNewCanonical('');
  }

  async function loadBooks() {
    setLoadingB(true);
    // Paged: held.length is the "Needs you" figure AND the sidebar queue
    // badge, so .limit(50) capped the badge at 50 however long the real
    // queue was — the one number that tells an admin there is work waiting.
    const [heldData, pubData] = await Promise.all([
      fetchAllRows(() => supabase.from('books').select('id, title, author_name, cover_image_url, description, status, submission_type, book_tags(tag_type, tropes(canonical_name))').eq('status', 'pending')),
      fetchAllRows(() => supabase.from('books').select('id, title, author_name, status, submission_type, book_tags(count)').eq('status', 'live')),
    ]);
    heldData.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    pubData.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    // Split held into under-5-tags, missing-description, and possible-dupes.
    //
    // Used to be tags.length < 5 ? 'tags' : 'dupe' — anything with enough tags
    // fell into the else and was labelled a possible duplicate, description or
    // not. A book held only because it has no description got offered a tag
    // editor (it already has 5+ tags) and a merge/keep-separate choice that
    // has nothing to do with why it's here. hasDescription mirrors the same
    // blank-aware check the DB's book_has_description() does — a whitespace-only
    // description is not a description.
    const processed = (heldData || []).map(b => {
      const tags = (b.book_tags || []).map(bt => bt.tropes?.canonical_name).filter(Boolean);
      const hasDescription = !!(b.description && b.description.trim());
      const holdReason = tags.length < 5 ? 'tags' : !hasDescription ? 'description' : 'dupe';
      return { ...b, tags, tagCount: tags.length, holdReason };
    });
    setHeld(processed);
    setPublished(pubData || []);
    setLoadingB(false);
  }

  // The queue badge and the "Needs you" figure are both just how many books
  // are held, so derive them from `held` rather than setting them by hand at
  // each site that changes it. Two places used to compute the same number
  // separately and could disagree; there is now one source.
  //
  // Skipped while loading so the badge does not flash 0 on mount before
  // loadBooks has returned.
  useEffect(() => {
    if (!loadingB) onBadgeChange?.('queue', held.length);
  }, [held.length, loadingB]);   // eslint-disable-line react-hooks/exhaustive-deps

  // Drop a book from the queue after a CONFIRMED write, never before one.
  //
  // Functional updater, deliberately. Up to six rows render at once and no
  // row disables its buttons while its write is in flight, so two actions can
  // be in the air together. Reading `held` from the closure meant the second
  // one filtered a snapshot taken before the first had applied, and its
  // setHeld then put the already-processed book back on screen. The database
  // was right and a reload fixed it, which is precisely what makes it the
  // kind of thing nobody reports.
  //
  // The badge is not set here at all — it is derived from `held` in the effect
  // above, so the count cannot disagree with the list it is counting.
  function dropFromQueue(id, toast) {
    setHeld(prev => prev.filter(b => b.id !== id));
    ping(toast);
  }

  function bookLabel(book, id) {
    if (!book) return `#${id}`;
    return `"${book.title}"${book.author_name ? ` by ${book.author_name}` : ''}`;
  }

  async function publishBook(id) {
    const book = held.find(b => b.id === id);
    const { error } = await supabase.from('books').update({ status: 'live' }).eq('id', id);
    if (error) { ping('Publish failed: ' + error.message); return; }
    await logAudit('create', 'book', `Published ${bookLabel(book, id)}`, id);
    dropFromQueue(id, 'Published');
  }

  // Reject writes 'removed', not 'rejected'.
  //
  // books.status is constrained to live / pending / flagged / removed
  // (books_status_check, 20260811000000_live_schema_snapshot.sql:1819). This
  // wrote 'rejected', which the constraint refuses, and the result was never
  // read — so every rejection was thrown away by the database while the row
  // vanished from the list and the toast said "Rejected". The book was still
  // pending and came back on the next load.
  //
  // 'removed' with a removal_reason is what BooksSection.jsx's own Reject has
  // always written for the same action; this now matches it rather than being
  // the odd one out.
  async function rejectBook(id) {
    const book = held.find(b => b.id === id);
    const reason = prompt('Rejection reason (optional):') ?? '';
    const { error } = await supabase
      .from('books')
      .update({ status: 'removed', removal_reason: reason || null })
      .eq('id', id);
    if (error) { ping('Reject failed: ' + error.message); return; }
    await logAudit('delete', 'book', `Rejected ${bookLabel(book, id)}, ${reason || 'no reason given'}`, id);
    dropFromQueue(id, 'Rejected');
  }

  // "Keep separate" is the opposite of a rejection: the admin has looked at a
  // book held as a possible duplicate and decided it is a distinct, legitimate
  // book. It was wired to rejectBook, which only ever appeared harmless
  // because that write always failed — the moment reject started working, this
  // button would have removed the book the admin just said to keep. It gets
  // its own handler and publishes, which is the same write "Merge" beside it
  // already performs. The audit line is what tells the two apart afterwards.
  async function keepSeparateBook(id) {
    const book = held.find(b => b.id === id);
    const { error } = await supabase.from('books').update({ status: 'live' }).eq('id', id);
    if (error) { ping('Keep separate failed: ' + error.message); return; }
    await logAudit('create', 'book', `Kept separate, not a duplicate: ${bookLabel(book, id)}`, id);
    dropFromQueue(id, 'Kept separate');
  }
  async function openEditPub(book) {
    // Fetch current tags for this book
    const data = await fetchAllRows(() => supabase
      .from('book_tags')
      .select('id, trope_id, tag_type, tropes(canonical_name)')
      .eq('book_id', book.id));
    setPubTags((data || []).map(t => ({ id: t.id, name: t.tropes?.canonical_name || '', trope_id: t.trope_id })));
    setEditPubBook(book);
    setPubTagInput('');
  }

  async function addPubTag() {
    if (!pubTagInput.trim() || !editPubBook) return;
    const name = pubTagInput.trim();

    // Now that this box reaches the matcher, it is an entry point into the
    // trope catalogue and needs the same 60-character floor as every other
    // one. Three rows in the live catalogue are whole paragraphs somebody
    // typed into a tag field; each is a permanent row nothing will ever match.
    // Refuse visibly and leave the text where it is, rather than truncating.
    const lengthProblem = tagNameError(name);
    if (lengthProblem) { ping(lengthProblem); return; }

    setSavingPubTag(true);

    // Only process-trope creates trope rows.
    //
    // This used to look the name up and, on a miss, INSERT it with
    // is_active true — a blind create landing straight in the live
    // catalogue, never compared against anything. That is the mechanism
    // that mints "HEA" beside an existing "Happily Ever After" and then
    // points the book at the new dead row: fully tagged, invisible in
    // discovery, nothing reporting a problem.
    //
    // resolveTagWithBucket asks the matcher and falls back to a direct
    // lookup when the edge function is unreachable. It returns no id rather
    // than creating anything, which is why the null branch below refuses
    // visibly instead of carrying on. It also takes the bucket off the tag it
    // matched, so a mood goes in as a mood and a content warning as a warning.
    // This box used to file every name as a trope.
    const { trope_id: tropeId, tagType, source } = await resolveTagWithBucket(name);
    if (!tropeId) {
      setSavingPubTag(false);
      ping(`Could not match "${name}" to a tag, nothing saved. Try again, or add it from the trope catalogue.`);
      return;   // the typed text stays in the box, so it is not lost
    }
    if (source === 'default') {
      setSavingPubTag(false);
      ping(unknownKindRefusal(name));
      return;
    }
    if (tagType === 'genre') {
      setSavingPubTag(false);
      ping(genreRefusal(name));
      return;
    }

    // source 'author', as the Books tag editor records an admin's tag
    // (BooksSection.jsx addTag): an admin adding a tag is acting for the book.
    // The author's edit form lists every tag on the book and its save replaces
    // only source = 'author' rows, so an 'author' tag can be unticked by the
    // author, where the old default ('api') could never be removed by them.
    const { data: newTag, error } = await supabase
      .from('book_tags')
      .insert({ book_id: editPubBook.id, trope_id: tropeId, tag_type: tagType, source: 'author' })
      .select('id')
      .single();
    if (error) {
      setSavingPubTag(false);
      ping('Tag not saved: ' + error.message);
      return;
    }

    setPubTags(t => [...t, { id: newTag?.id, name, trope_id: tropeId }]);
    setPubTagInput('');
    setSavingPubTag(false);
  }

  async function removePubTag(tagId) {
    await supabase.from('book_tags').delete().eq('id', tagId);
    setPubTags(t => t.filter(x => x.id !== tagId));
  }

  async function addTagToBook(bookId, tag) {
    const name = tag.trim();
    if (!name) return;

    // Same length guard as addPubTag, for the same reason: this box now
    // reaches the matcher too.
    const lengthProblem = tagNameError(name);
    if (lengthProblem) { ping(lengthProblem); return; }

    // Same rule as addPubTag above: only process-trope creates trope rows.
    // This site had the identical blind insert, on the approval queue rather
    // than the published-book editor.
    // The bucket comes from the tag the name matched, as in addPubTag above.
    const { trope_id: tropeId, tagType, source } = await resolveTagWithBucket(name);
    if (!tropeId) {
      ping(`Could not match "${name}" to a tag, nothing saved. Try again, or add it from the trope catalogue.`);
      return;   // the typed text stays in the box, so it is not lost
    }
    if (source === 'default') { ping(unknownKindRefusal(name)); return; }
    if (tagType === 'genre') { ping(genreRefusal(name)); return; }

    const { error } = await supabase
      .from('book_tags')
      .upsert({ book_id: bookId, trope_id: tropeId, tag_type: tagType, source: 'author' }, { onConflict: 'book_id,trope_id', ignoreDuplicates: true });
    if (error) { ping('Tag not saved: ' + error.message); return; }

    setHeld(h => h.map(b => b.id === bookId ? { ...b, tags: [...b.tags, name], tagCount: b.tagCount + 1 } : b));
    setNewTag('');
    ping('Tag added');
  }

  // Goes through set_reader_description(), not a plain .update() — a bare
  // write here would leave description_source null. is_author_described
  // requires description_source = 'author', so an unattributed description
  // can never reach the swipe deck, and nothing downstream could later tell
  // a reader/admin-entered description from an author's. The RPC records it
  // as 'reader' (an admin transcribing one in is not the author either) and
  // only promotes into books.description when the book has none yet — which
  // is always true here, since that is why the book is in this bucket. Same
  // trigger that promotes a book at 5 tags then promotes this one too, on
  // its next reload.
  async function addDescriptionToBook(bookId, text) {
    if (!text.trim()) return;
    // `data: promo` rather than shorthand `{ data, error }`: check-silent-writes
    // only recognises the named form as a checked write.
    const { data: promo, error } = await supabase.rpc('set_reader_description', { p_book_id: bookId, p_text: text.trim() });
    if (error) { ping('Save failed: ' + error.message); return; }
    if (!promo?.promoted) {
      // Ticket 66: the book already had a description, so nothing replaced it.
      // But when the words were archived into reader_description they WERE
      // saved, and the matched text changed -- so re-embed, and say the true
      // thing in one message: saved as reader text, plus the re-embed failure
      // if there was one. "Not saved" is only right when nothing was written.
      if (promo?.archived) {
        const reArch = await requestBookReembed(bookId);
        ping(reArch.ok
          ? 'Saved as reader text (this book already has a description)'
          : `Saved as reader text (this book already has a description), but the book could not be re-embedded yet (${reArch.reason})`);
        return;
      }
      ping('Not saved, this book already has a description');
      return;
    }
    setHeld(h => h.map(b => b.id === bookId ? { ...b, description: text.trim() } : b));
    setNewDescription('');
    // Ticket 66: the new description changes the text the book is matched on,
    // so embed it now rather than leaving it "saved but not searchable". The
    // description HAS saved; a failed embed is reported as its own problem and
    // never as the save having failed.
    const re = await requestBookReembed(bookId);
    ping(re.ok ? 'Description added' : `Description added, but the book could not be re-embedded yet (${re.reason})`);
  }
  const SubTab = ({ k, label, count }) => (
    <button onClick={() => setTab(k)} style={{ padding: '9px 18px', background: 'none', border: 'none', borderBottom: tab === k ? '2px solid var(--neutral-800)' : '2px solid transparent', marginBottom: -2, cursor: 'pointer', fontSize: 12.5, fontWeight: tab === k ? 700 : 600, color: tab === k ? 'var(--neutral-800)' : 'var(--neutral-400)', display: 'flex', alignItems: 'center', gap: 'var(--space-6)' }}>
      {label}
      <span style={{ fontSize: 'var(--font-size-tiny)', fontWeight: 700, color: 'var(--brand-white)', background: tab === k ? 'var(--brand-rust)' : 'var(--neutral-300)', padding: '1px 7px', borderRadius: 'var(--radius-md)' }}>{count}</span>
    </button>
  );

  const SRC_COLOR = { author: { bg: '#eff6ff', c: '#2563eb', b: '#bfdbfe' }, reader: { bg: '#ecfdf5', c: '#059669', b: '#a7f3d0' }, csv: { bg: '#f5f0e8', c: '#8b7355', b: '#e8dcc8' } };
  const SourceChip = ({ type }) => {
    const s = SRC_COLOR[type] || SRC_COLOR.reader;
    return <span style={{ fontSize: 'var(--font-size-tiny)', fontWeight: 700, color: s.c, background: s.bg, border: `1px solid ${s.b}`, padding: '2px 8px', borderRadius: 'var(--radius-md)' }}>{(type || 'reader').toUpperCase()}</span>;
  };

  return (
    <div style={{ padding: '30px 36px', fontFamily: 'var(--font-body)' }}>
      <div style={{ marginBottom: 18 }}>
        <h1 style={{ fontFamily: 'var(--font-display)', fontSize: 'var(--font-size-h4)', fontWeight: 700, color: 'var(--neutral-800)', margin: 'var(--space-none)' }}>Approval Queue</h1>
        <p style={{ fontSize: 'var(--font-size-base)', color: '#8b7355', margin: '4px 0 0' }}>Most submissions auto-publish. Only books under 5 tags, missing a description, or a possible duplicate surface here.</p>
      </div>

      {/* Sub-tabs */}
      <div style={{ display: 'flex', gap: 'var(--space-none)', borderBottom: '2px solid #ece3d4', marginBottom: 'var(--space-xl)' }}>
        <SubTab k="books"    label="📥 Books"    count={held.length} />
        <SubTab k="services" label="💼 Services"  count={svcReview.length + suggestions.length} />
      </div>

      {/* ── BOOKS TAB ── */}
      {tab === 'books' && (
        <>
          <div style={{ background: 'var(--feedback-success-bg)', border: '1px solid #a7f3d0', borderRadius: 'var(--radius-10)', padding: '10px 14px', marginBottom: 'var(--space-lg)' }}>
            <p style={{ fontSize: 11.5, color: '#065f46', margin: 'var(--space-none)', lineHeight: 1.5 }}>
              Books <strong>auto-publish</strong> the moment they're submitted. Only three things land here: submissions with <strong>under 5 tags</strong>, submissions with <strong>no description</strong>, and <strong>possible duplicates</strong>. Everything else is in Recently published below.
            </p>
          </div>

          {/* Held books */}
          <p style={{ fontSize: 'var(--font-size-xxs)', fontWeight: 700, letterSpacing: 1, color: '#8b7355', textTransform: 'uppercase', margin: '0 0 8px' }}>Needs you · {held.length}</p>
          {loadingB ? (
            <div style={{ padding: 'var(--space-h4)', textAlign: 'center', color: 'var(--neutral-400)', fontSize: 'var(--font-size-base)' }}>Loading...</div>
          ) : held.length === 0 ? (
            <div style={{ padding: 'var(--space-h4)', textAlign: 'center', color: 'var(--neutral-400)', fontSize: 'var(--font-size-base)', background: 'var(--brand-white)', borderRadius: 'var(--radius-lg)', border: '1px solid #ece3d4', marginBottom: 'var(--space-xxl)' }}>Nothing held, everything is auto-publishing.</div>
          ) : (
            <div style={{ border: '1px solid #ece3d4', borderRadius: 14, background: '#fdfbf7', padding: 'var(--space-14)', marginBottom: 'var(--space-xxl)' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-10)', minHeight: 280 }}>
                {held.slice(0, 6).map(b => (
                  <div key={b.id} style={{ background: 'var(--brand-white)', borderRadius: 14, border: b.holdReason === 'dupe' ? '1px solid #e9d5ff' : b.holdReason === 'description' ? '1px solid #bfdbfe' : '1px solid var(--feedback-warning-border)', padding: isMobile ? 12 : 16, display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: isMobile ? 10 : 14 }}>
                    <div style={{ width: isMobile ? 40 : 48, height: isMobile ? 58 : 68, borderRadius: '3px 5px 5px 3px', background: 'var(--brand-sand)', flexShrink: 0, overflow: 'hidden' }}>
                      {b.cover_image_url
                        ? <img src={b.cover_image_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={onCoverError} />
                        : <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 'var(--font-size-xxl)' }}>📚</div>}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 'var(--space-6)' }}>
                        <div>
                          <p style={{ fontFamily: 'var(--font-display)', fontSize: 15, fontWeight: 700, color: 'var(--neutral-800)', margin: '0 0 1px' }}>{b.title}</p>
                          <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--neutral-500)', margin: 'var(--space-none)' }}>by {b.author_name}</p>
                        </div>
                        {b.holdReason === 'tags'
                          ? <span style={{ fontSize: 'var(--font-size-tiny)', fontWeight: 700, color: 'var(--feedback-warning-ink)', background: 'var(--feedback-warning-bg)', border: '1px solid var(--feedback-warning-border)', padding: '2px 8px', borderRadius: 'var(--radius-md)', flexShrink: 0 }}>⚠ {b.tagCount}/5 TAGS</span>
                          : b.holdReason === 'description'
                          ? <span style={{ fontSize: 'var(--font-size-tiny)', fontWeight: 700, color: '#2563eb', background: '#eff6ff', border: '1px solid #bfdbfe', padding: '2px 8px', borderRadius: 'var(--radius-md)', flexShrink: 0 }}>MISSING DESCRIPTION</span>
                          : <span style={{ fontSize: 'var(--font-size-tiny)', fontWeight: 700, color: '#7c3aed', background: '#f3e8ff', border: '1px solid #d8b4fe', padding: '2px 8px', borderRadius: 'var(--radius-md)', flexShrink: 0 }}>POSSIBLE DUPLICATE</span>}
                      </div>
                      <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', marginBottom: 'var(--space-6)' }}>
                        {b.tags.map(t => <span key={t} style={{ fontSize: 'var(--font-size-xxs)', padding: '3px 9px', borderRadius: 'var(--radius-10)', background: 'var(--feedback-warning-bg)', color: 'var(--feedback-warning-ink)', border: '1px solid var(--feedback-warning-border)' }}>{t}</span>)}
                      </div>
                      <div style={{ display: 'flex', gap: 'var(--space-6)', alignItems: 'center' }}>
                        <SourceChip type={b.submission_type} />
                      </div>
                      {/* Add tag inline */}
                      {editingId === b.id && b.holdReason === 'tags' && (
                        <div style={{ display: 'flex', gap: 'var(--space-6)', marginTop: 'var(--space-sm)' }}>
                          <input value={newTag} onChange={e => setNewTag(e.target.value)}
                            onKeyDown={e => { if (e.key === 'Enter') { addTagToBook(b.id, newTag); } }}
                            placeholder="Type a tag + Enter"
                            style={{ flex: 1, padding: '6px 10px', border: '1px solid var(--feedback-warning-border)', borderRadius: 'var(--radius-md)', fontSize: 'var(--font-size-sm)', outline: 'none', fontFamily: 'var(--font-body)' }} />
                          <button onClick={() => addTagToBook(b.id, newTag)} style={{ padding: '6px 12px', background: 'var(--neutral-800)', color: 'var(--brand-white)', border: 'none', borderRadius: 'var(--radius-md)', fontSize: 'var(--font-size-xs)', fontWeight: 600, cursor: 'pointer' }}>Add</button>
                        </div>
                      )}
                      {/* Add description inline */}
                      {editingId === b.id && b.holdReason === 'description' && (
                        <div style={{ display: 'flex', gap: 'var(--space-6)', marginTop: 'var(--space-sm)' }}>
                          <textarea value={newDescription} onChange={e => setNewDescription(e.target.value)}
                            placeholder="What's this book about?" rows={3}
                            style={{ flex: 1, padding: '6px 10px', border: '1px solid #bfdbfe', borderRadius: 'var(--radius-md)', fontSize: 'var(--font-size-sm)', outline: 'none', fontFamily: 'var(--font-body)', resize: 'vertical' }} />
                          <button onClick={() => addDescriptionToBook(b.id, newDescription)} style={{ padding: '6px 12px', background: 'var(--neutral-800)', color: 'var(--brand-white)', border: 'none', borderRadius: 'var(--radius-md)', fontSize: 'var(--font-size-xs)', fontWeight: 600, cursor: 'pointer', alignSelf: 'flex-start' }}>Save</button>
                        </div>
                      )}
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap', gap: 'var(--space-6)', justifyContent: 'flex-start', flexShrink: 0, marginTop: isMobile ? 4 : 0 }}>
                      {b.holdReason === 'tags' ? (
                        <>
                          <button onClick={() => setEditingId(editingId === b.id ? null : b.id)} style={{ padding: '7px 14px', background: 'var(--brand-forest)', color: 'var(--brand-white)', border: 'none', borderRadius: 9, fontSize: 'var(--font-size-xs)', fontWeight: 700, cursor: 'pointer' }}>Add tags</button>
                          <button onClick={() => publishBook(b.id)} style={{ padding: '7px 14px', background: 'var(--brand-white)', color: 'var(--neutral-600)', border: '1px solid var(--brand-sand)', borderRadius: 9, fontSize: 'var(--font-size-xs)', fontWeight: 600, cursor: 'pointer' }}>Publish anyway</button>
                          <button onClick={() => rejectBook(b.id)} style={{ padding: '7px 14px', background: 'var(--brand-white)', color: 'var(--feedback-error)', border: '1px solid #fecaca', borderRadius: 9, fontSize: 'var(--font-size-xs)', fontWeight: 600, cursor: 'pointer' }}>Reject</button>
                        </>
                      ) : b.holdReason === 'description' ? (
                        <>
                          <button onClick={() => setEditingId(editingId === b.id ? null : b.id)} style={{ padding: '7px 14px', background: '#2563eb', color: 'var(--brand-white)', border: 'none', borderRadius: 9, fontSize: 'var(--font-size-xs)', fontWeight: 700, cursor: 'pointer' }}>Add description</button>
                          <button onClick={() => publishBook(b.id)} style={{ padding: '7px 14px', background: 'var(--brand-white)', color: 'var(--neutral-600)', border: '1px solid var(--brand-sand)', borderRadius: 9, fontSize: 'var(--font-size-xs)', fontWeight: 600, cursor: 'pointer' }}>Publish anyway</button>
                          <button onClick={() => rejectBook(b.id)} style={{ padding: '7px 14px', background: 'var(--brand-white)', color: 'var(--feedback-error)', border: '1px solid #fecaca', borderRadius: 9, fontSize: 'var(--font-size-xs)', fontWeight: 600, cursor: 'pointer' }}>Reject</button>
                        </>
                      ) : (
                        <>
                          <button onClick={() => publishBook(b.id)} style={{ padding: '7px 14px', background: '#7c3aed', color: 'var(--brand-white)', border: 'none', borderRadius: 9, fontSize: 'var(--font-size-xs)', fontWeight: 700, cursor: 'pointer' }}>Merge</button>
                          <button onClick={() => keepSeparateBook(b.id)} style={{ padding: '7px 14px', background: 'var(--brand-white)', color: 'var(--neutral-600)', border: '1px solid var(--brand-sand)', borderRadius: 9, fontSize: 'var(--font-size-xs)', fontWeight: 600, cursor: 'pointer' }}>Keep separate</button>
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Recently published auto-live log */}
          <p style={{ fontSize: 'var(--font-size-xxs)', fontWeight: 700, letterSpacing: 1, color: '#8b7355', textTransform: 'uppercase', margin: '0 0 8px' }}>Recently published · auto-live</p>
          <div style={{ border: '1px solid #ece3d4', borderRadius: 14, background: '#fdfbf7', padding: 'var(--space-14)' }}>
            <div style={{ display: 'flex', flexDirection: 'column', minHeight: 200 }}>
              {(published || []).slice(0, 9).map((p, i) => (
                <div key={p.id} style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-md)', padding: '11px 4px', borderBottom: i < Math.min(published.length, 9) - 1 ? '1px solid var(--brand-cream)' : 'none' }}>
                  <div style={{ flex: 1 }}>
                    <span style={{ fontSize: 'var(--font-size-base)', fontWeight: 700, color: 'var(--neutral-800)' }}>{p.title}</span>
                    <span style={{ fontSize: 'var(--font-size-xxs)', color: 'var(--neutral-400)', marginLeft: 'var(--space-sm)' }}>{p.author_name}</span>
                  </div>
                  {!isMobile && <SourceChip type={p.submission_type} />}
                  {p.submission_type === 'csv' && (
                    <span style={{ fontSize: 8.5, fontWeight: 700, color: 'var(--feedback-warning-ink)', background: 'var(--feedback-warning-bg)', border: '1px solid var(--feedback-warning-border)', padding: '2px 7px', borderRadius: 7 }}>UNVERIFIED</span>
                  )}
                  <button onClick={() => openEditPub(p)} style={{ padding: '5px 12px', background: 'var(--brand-white)', color: 'var(--neutral-600)', border: '1px solid var(--brand-sand)', borderRadius: 'var(--radius-md)', fontSize: 10.5, fontWeight: 600, cursor: 'pointer', flexShrink: 0 }}>Edit tags</button>
                </div>
              ))}
              {published.length === 0 && !loadingB && <div style={{ padding: '30px', textAlign: 'center', color: 'var(--neutral-400)', fontSize: 'var(--font-size-base)' }}>No published books yet.</div>}
            </div>
          </div>
        </>
      )}

      {/* ── SERVICES TAB ── */}
      {tab === 'services' && (
        <div>
          <div style={{ background: '#fef9ec', border: '1px solid var(--feedback-warning-border)', borderRadius: 'var(--radius-10)', padding: '10px 14px', marginBottom: 'var(--space-14)' }}>
            <p style={{ fontSize: 11.5, color: 'var(--feedback-warning-ink)', margin: 'var(--space-none)' }}>Unmatched services, non-USD rates, and unparseable rates. They still display on profiles. Mapping applies to every reader who typed the same text, whatever the capitalisation.</p>
          </div>

          {/* NEW — AI-suggested matches from process-service. Suggest-only: nothing here has ever auto-applied. */}
          {suggestions.length > 0 && (
            <div style={{ marginBottom: 'var(--space-xl)' }}>
              <p style={{ fontSize: 'var(--font-size-xxs)', fontWeight: 700, letterSpacing: 1, color: '#8b7355', textTransform: 'uppercase', margin: '0 0 8px' }}>
                Suggested matches · {suggestions.length}
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-sm)' }}>
                {suggestions.map(s => (
                  <div key={s.id} style={{ border: '1px solid #d8b4fe', background: '#faf5ff', borderRadius: 'var(--radius-10)', padding: '12px 14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 'var(--space-md)', flexWrap: 'wrap' }}>
                    <div>
                      <p style={{ fontSize: 'var(--font-size-base)', margin: '0 0 3px' }}>
                        <strong>"{s.original_name}"</strong> → <strong style={{ color: '#7c3aed' }}>{s.matched_service_name}</strong>
                      </p>
                      <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--neutral-400)', margin: 'var(--space-none)' }}>
                        @{s.username || 'unknown'} · {Math.round(s.similarity_score * 100)}% match · via {s.method}
                      </p>
                    </div>
                    <div style={{ display: 'flex', gap: 'var(--space-6)', flexShrink: 0 }}>
                      <button onClick={() => approveSuggestion(s)} style={{ padding: '6px 14px', background: '#7c3aed', color: 'var(--brand-white)', border: 'none', borderRadius: 'var(--radius-md)', fontSize: 'var(--font-size-xs)', fontWeight: 700, cursor: 'pointer' }}>
                        Approve
                      </button>
                      <button onClick={() => rejectSuggestion(s)} style={{ padding: '6px 14px', background: 'var(--brand-white)', color: 'var(--neutral-500)', border: '1px solid var(--brand-sand)', borderRadius: 'var(--radius-md)', fontSize: 'var(--font-size-xs)', fontWeight: 600, cursor: 'pointer' }}>
                        Reject
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Add new canonical — writes to canonical_services table so it persists */}
          <div style={{ display: 'flex', gap: 'var(--space-sm)', marginBottom: 'var(--space-14)' }}>
            <input value={newCanonical} onChange={e => setNewCanonical(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && addCanonicalService(newCanonical)}
              placeholder="Promote custom service to canonical (e.g. Audiobook Narration)"
              style={{ flex: 1, padding: '8px 12px', border: '1px solid var(--brand-sand)', borderRadius: 'var(--radius-md)', fontSize: 'var(--font-size-sm)', outline: 'none' }} />
            <button onClick={() => addCanonicalService(newCanonical)}
              style={{ padding: '8px 14px', background: 'var(--brand-forest)', color: 'var(--brand-white)', border: 'none', borderRadius: 'var(--radius-md)', fontSize: 'var(--font-size-sm)', cursor: 'pointer', fontWeight: 600 }}>
              Add canonical
            </button>
          </div>
          {loadingSvc && <p style={{ color: 'var(--neutral-400)', fontSize: 'var(--font-size-base)', textAlign: 'center', padding: '20px 0' }}>Loading...</p>}
          {!loadingSvc && svcReview.length === 0 && <div style={{ padding: 'var(--space-xxl)', textAlign: 'center', color: 'var(--neutral-400)', fontSize: 'var(--font-size-sm)' }}>✓ No services need review.</div>}
          {svcReview.map((r, i) => (
            <div key={i} style={{ border: '1px solid var(--brand-sand)', borderRadius: 'var(--radius-10)', padding: '12px 14px', marginBottom: 'var(--space-sm)', background: 'var(--brand-white)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 'var(--space-md)', flexWrap: 'wrap' }}>
                <div>
                  <p style={{ fontSize: 'var(--font-size-base)', fontWeight: 700, color: 'var(--neutral-800)', margin: '0 0 3px' }}>"{r.service}"</p>
                  <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', marginBottom: 'var(--space-xxs)' }}>
                    {(Array.isArray(r.reasons) ? r.reasons : [r.reason]).map(reason => (
                      <span key={reason} style={{ fontSize: 'var(--font-size-xxs)', fontWeight: 700, padding: '2px 7px', borderRadius: 6,
                        background: reason === 'Unmatched service' ? 'var(--feedback-warning-bg)' : reason === 'Non-USD currency' ? '#ede9fe' : 'var(--feedback-error-bg)',
                        color: reason === 'Unmatched service' ? 'var(--feedback-warning-ink)' : reason === 'Non-USD currency' ? '#5b21b6' : '#991b1b' }}>
                        {reason}
                      </span>
                    ))}
                  </div>
                  <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--neutral-400)', margin: 'var(--space-none)' }}>
                    @{r.username}
                    {r.rate && <span style={{ marginLeft: 'var(--space-sm)' }}>Rate: {r.rate}</span>}
                    {r.currency && r.currency !== '$' && <span style={{ marginLeft: 'var(--space-sm)', color: '#5b21b6' }}>({r.currency})</span>}
                  </p>
                </div>
                <div style={{ display: 'flex', gap: 'var(--space-6)', flexShrink: 0 }}>
                  <select defaultValue=""
                    onChange={e => e.target.value && mapServiceToCanonical(r.service, e.target.value)}
                    style={{ padding: '5px 8px', border: '1px solid var(--brand-sand)', borderRadius: 'var(--radius-md)', fontSize: 'var(--font-size-sm)', cursor: 'pointer' }}>
                    <option value="">Map all to canonical…</option>
                    {canonicalList.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                  <button onClick={() => dismissService(r.user_id, r.service)}
                    style={{ padding: '5px 10px', background: 'var(--neutral-100)', border: 'none', borderRadius: 'var(--radius-md)', fontSize: 'var(--font-size-xs)', color: 'var(--neutral-500)', cursor: 'pointer' }}>
                    Keep as custom
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── TROPES TAB ── */}

      {toast && <div style={{ position: 'fixed', bottom: 28, left: '50%', transform: 'translateX(-50%)', zIndex: 300, background: 'var(--brand-forest)', color: 'var(--brand-white)', padding: '12px 22px', borderRadius: 14, boxShadow: '0 8px 24px rgba(0,0,0,0.2)', fontSize: 'var(--font-size-base)', fontWeight: 600 }}>{toast}</div>}

      {/* Published book tag editor modal */}
      {editPubBook && (
        <div onClick={() => setEditPubBook(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 400, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 'var(--space-xl)' }}>
          <div onClick={e => e.stopPropagation()} style={{ background: 'var(--brand-white)', borderRadius: 'var(--radius-xl)', maxWidth: 520, width: '100%', padding: 'var(--space-xxl)', maxHeight: '80vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.25)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 'var(--space-lg)' }}>
              <div>
                <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 'var(--font-size-xl)', fontWeight: 700, color: 'var(--neutral-800)', margin: '0 0 2px' }}>{editPubBook.title}</h3>
                <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--neutral-500)', margin: 'var(--space-none)' }}>by {editPubBook.author_name}</p>
              </div>
              <button onClick={() => setEditPubBook(null)} style={{ background: 'var(--neutral-100)', border: 'none', width: 30, height: 30, borderRadius: 'var(--radius-circle)', cursor: 'pointer', fontSize: 'var(--font-size-lg)', color: 'var(--neutral-500)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>×</button>
            </div>

            {/* Current tags */}
            <p style={{ fontSize: 'var(--font-size-xs)', fontWeight: 700, color: '#8b7355', textTransform: 'uppercase', letterSpacing: 0.5, margin: '0 0 10px' }}>
              Tags · {pubTags.length}
            </p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-6)', marginBottom: 'var(--space-14)', minHeight: 36 }}>
              {pubTags.length === 0 && <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--neutral-400)', fontStyle: 'italic' }}>No tags yet</span>}
              {pubTags.map(t => (
                <span key={t.id} style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--space-xs)', padding: '4px 10px', background: 'var(--feedback-warning-bg)', color: 'var(--feedback-warning-ink)', borderRadius: 'var(--radius-xxl)', fontSize: 'var(--font-size-sm)', border: '1px solid var(--feedback-warning-border)' }}>
                  {t.name}
                  <span onClick={() => removePubTag(t.id)} style={{ cursor: 'pointer', fontSize: 'var(--font-size-xs)', fontWeight: 700 }}>×</span>
                </span>
              ))}
            </div>

            {/* Add tag */}
            <div style={{ display: 'flex', gap: 'var(--space-sm)' }}>
              <input
                value={pubTagInput}
                onChange={e => setPubTagInput(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') addPubTag(); }}
                placeholder="Add a tag..."
                style={{ flex: 1, padding: '9px 12px', border: '1px solid var(--brand-sand)', borderRadius: 9, fontSize: 'var(--font-size-base)', outline: 'none', fontFamily: 'var(--font-body)' }}
              />
              <button onClick={addPubTag} disabled={savingPubTag || !pubTagInput.trim()} style={{ padding: '9px 16px', background: savingPubTag ? 'var(--brand-sand)' : 'var(--neutral-800)', color: savingPubTag ? 'var(--neutral-400)' : 'var(--brand-white)', border: 'none', borderRadius: 9, fontSize: 'var(--font-size-sm)', fontWeight: 600, cursor: savingPubTag ? 'default' : 'pointer' }}>
                {savingPubTag ? '...' : 'Add'}
              </button>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 18 }}>
              <button onClick={() => setEditPubBook(null)} style={{ padding: '10px 20px', background: 'var(--neutral-800)', color: 'var(--brand-white)', border: 'none', borderRadius: 'var(--radius-10)', fontSize: 'var(--font-size-base)', fontWeight: 700, cursor: 'pointer' }}>Done</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function AdminPortal() {
  const navigate = useNavigate();
  const { section } = useParams();
  const active = section || 'queue';

  const [adminName,    setAdminName]    = useState('');
  const [badges,       setBadges]       = useState({});
  const [sidebarOpen,  setSidebarOpen]  = useState(false);
const [isMobile,     setIsMobile]     = useState(() => typeof window !== 'undefined' ? window.innerWidth < NARROW_PX : false);

  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < NARROW_PX);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  useEffect(() => {
    async function loadMeta() {
      // Fix: getCurrentUser avoids AbortError from supabase.auth.getUser()
      const user = await getCurrentUser();
      if (!user) return;

      const { data: profile } = await supabase
        .from('user_profiles')
        .select('display_name, username')
        .eq('user_id', user.id)
        .single();
      if (profile) setAdminName(profile.display_name || profile.username || 'Admin');

      const [
        tropeRes,
        bookRes,
        dupePairs,
        openReports,
        feedbackRes,
        authorPendingRes,
        subgenrePendingRes,
      ] = await Promise.all([
        supabase.from('tropes').select('*', { count: 'exact', head: true }).eq('is_active', false).in('merge_status', ['active', 'pending_review']),
        supabase.from('books').select('*', { count: 'exact', head: true }).eq('status', 'pending'),
        // Duplicate pairs waiting for a decision: counted by the one function the
        // Duplicates tab, the Trope Queue and the Overview all use, so the badge
        // says the same number the tab shows once it is opened.
        loadDuplicatePairs(),
        // One read of the open reports feeds two badges, each counted the way its
        // own screen counts it once opened: Reports groups several reports on one
        // thing into one card, Users counts users with any open report about them.
        fetchAllRowsResult(() => supabase.from('reports').select('id, entity_type, entity_id, reported_user_id').eq('status', 'open')),
        // Fix: no .catch() — it throws and stops all badges from loading
        supabase.from('feedback').select('*', { count: 'exact', head: true }).eq('status', 'new'),
        // Fix: count pending author-submitted books, not suspended profiles
        supabase.from('books').select('*', { count: 'exact', head: true }).eq('status', 'pending').eq('submission_type', 'author'),
        // PR 4 review fix: this badge was never counted here at all, so the
        // sidebar's red dot only ever appeared once someone opened the
        // section and it reported its own count.
        supabase.from('subgenres').select('*', { count: 'exact', head: true }).eq('status', 'pending'),
      ]);

      // A count whose read was refused is null, never 0: 0 says "nothing waiting",
      // null says "could not load". The More menu reads null as the second.
      const cnt = (r) => (r && !r.error && typeof r.count === 'number' ? r.count : null);
      // Services badge — count upfront so it shows without opening the tab
      const allSvcProfiles = await fetchAllProfilesWithServices();
      const svcCount = buildServiceReviewRows(allSvcProfiles).length;

      // PR 4 review fix: merge into whatever's already there instead of
      // replacing the whole object — SubgenreQueueSection can report its own
      // count (onBadgeChange('subgenres', n)) before this Promise.all lands,
      // and a plain setBadges({...}) here was wiping that key back out.
      // A failed read of the reports leaves those two badges alone rather than
      // writing a too-low number into them.
      const pendingTropes = cnt(tropeRes);
      const counted = {
        reports:   openReports.ok ? new Set(openReports.rows.map(r => `${r.entity_type}:${r.entity_id}`)).size : null,
        users:     openReports.ok ? new Set(openReports.rows.filter(r => r.entity_type === 'user' && r.reported_user_id).map(r => r.reported_user_id)).size : null,
        queue:     cnt(bookRes),
        tropes:    pendingTropes !== null && dupePairs.ok ? pendingTropes + dupePairs.pairs.length : null,
        feedbacks: cnt(feedbackRes),
        authors:   cnt(authorPendingRes),
        subgenres: cnt(subgenrePendingRes),
      };

      // A failed read never overwrites a number a screen has already reported: it
      // only fills a gap, with null.
      setBadges(b => {
        const next = { ...b, services: svcCount || 0 };
        for (const [key, val] of Object.entries(counted)) {
          if (val !== null) next[key] = val;
          else if (next[key] === undefined) next[key] = null;
        }
        return next;
      });
    }
    loadMeta();
  }, []);

  // Escape closes the narrow-screen drawer.
  useEffect(() => {
    if (!sidebarOpen) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setSidebarOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [sidebarOpen]);

  const total = panelTotal(badges);
  const here  = sectionCount(active, badges);
  // Some of the counts behind the total could not load. The total is then only
  // what the other counts found, so the More badge says so.
  const totalIncomplete = failedParts(badges).length > 0;

  function handleBadgeChange(key, val) {
    setBadges(b => ({ ...b, [key]: val }));
  }

function handleNav(id) {
    // Something on this screen is not saved: ask before leaving (ticket 118, src/lib/unsavedChanges.js).
    // The item for the screen already open is not a way off, so it asks nothing.
    if (id !== active && !okToLeave()) return;
    navigate(`/admin/${id}`);
    if (isMobile) setSidebarOpen(false);
  }

  // onNavigate was declared by TropeCatalogSection and never passed, so its
  // "Review in queue" button called onNavigate?.() and silently did nothing.
  // isMobile goes to every screen from here, so the shell and its screens switch
  // layout at the same width (NARROW_PX). No screen keeps its own width check.
  const p = { onBadgeChange: handleBadgeChange, onNavigate: handleNav, isMobile };

  function renderSection() {
    // A sidebar item marked soon has no component. Every one opens the same
    // screen, which says what it will be and what it is waiting on.
    const navItem = findNavItem(active);
    if (navItem?.soon) return <SoonScreen item={navItem} isMobile={isMobile} />;

    switch (active) {
      // ── Overview ────────────────────────────────────────────────────
      case 'overview':    return <OverviewSection {...p} adminUsername={adminName} />;

      // ── Moderation ──────────────────────────────────────────────────
      case 'queue':       return <ApprovalQueueSection {...p} isMobile={isMobile} />;
      case 'reports':     return <ReportsSection {...p} />;
      case 'feedbacks':   return <FeedbackSection {...p} />;
      case 'users':       return <AdminUsersSection {...p} />;

      // ── Reader ───────────────────────────────────────────────────────
      case 'r-readers':   return <ReadersSection {...p} />;
      case 'r-books':     return <BooksSection key="r-books" {...p} defaultTab="published" />;
            case 'r-pipelines': return (
        <>
          <AdminPipelinesSection {...p} isMobile={isMobile} />
          <CompClaimsSection {...p} />
        </>
      );
      // ── Discovery ───────────────────────────────────────────────────
      case 'health':      return <HealthSection {...p} />;
      case 'cravings':    return <CravingsScreen {...p} />;

      case 'r-quotes':    return <QuotesSection {...p} />;
      case 'r-giveaway':  return <GiveawaySection {...p} />;

      // ── Author ───────────────────────────────────────────────────────
      case 'a-authors':   return <AdminAuthorSection {...p} />;
      case 'a-books':     return <BooksSection key="a-books" {...p} defaultTab="published" filterSource="author" />;

      // ── Config ───────────────────────────────────────────────────────
      case 'c-trope-queue': return <TropeQueueSection {...p} />;
      case 'c-genre-queue':  return <SubgenreQueueSection {...p} />;
      case 'c-tropes':    return <TropeCatalogSection {...p} />;
      case 'affiliate':   return <AffiliateSection {...p} />;
      case 'c-legal':     return <LegalPagesSection {...p} />;
      case 'c-csv':      return <CsvSection {...p} />;
      case 'c-events':    return <EventsSection {...p} />;
      case 'c-event-import': return <AdminEventImporter {...p} />;
      case 'analytics':   return <AnalyticsSection {...p} />;
      case 'audit':       return <AuditLogSection {...p} />;
      case 'prompts':     return <JournalPromptsSection {...p} />;
      case 'reflections': return <JournalSection {...p} />;
      case 'settings':    return <SettingsSection {...p} />;

 default:
  return (
    <div style={{ padding: 'clamp(16px, 3vw, 36px)', fontFamily: 'DM Sans, sans-serif' }}>
      <h1 style={{ fontFamily: 'Georgia, serif', fontSize: 'var(--font-size-h4)', fontWeight: 700, color: 'var(--neutral-800)', margin: '0 0 8px' }}>
        Section not found
      </h1>
      <p style={{ fontSize: 'var(--font-size-md)', color: 'var(--neutral-500)', margin: '0 0 20px' }}>
        "{active}" isn't a section of the admin panel, it may have been
        renamed or the link is out of date.
      </p>
      <button
        onClick={() => navigate('/admin/queue')}
        style={{ padding: '10px 20px', background: 'var(--neutral-800)', color: 'var(--brand-white)', border: 'none', borderRadius: 'var(--radius-10)', fontSize: 'var(--font-size-base)', fontWeight: 600, cursor: 'pointer', fontFamily: 'DM Sans, sans-serif' }}
      >
        Go to Approval Queue
      </button>
    </div>
  );
    }
  }

  return (
    <AdminGuard>
      <div style={{ display: 'flex', height: '100vh', overflow: 'hidden', background: '#FAF7F1', fontFamily: 'var(--font-body)' }}>
        <AdminSidebar active={active} onNav={handleNav} badges={badges} adminName={adminName} isMobile={isMobile} sidebarOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
        <div style={{ flex: 1, overflowY: 'auto', minWidth: 0, display: 'flex', flexDirection: 'column' }}>
          {/* Mobile top bar: the section name, with only that section's own count ("N here").
              No logo: the app's own header sits above this one. */}
          {isMobile && (
            <div style={{ position: 'sticky', top: 0, zIndex: 100, background: 'var(--brand-white)', borderBottom: '1px solid var(--brand-sand)', padding: '8px 16px', minHeight: 44, display: 'flex', alignItems: 'center', gap: 12, flexShrink: 0 }}>
              <div style={{ flex: 1, minWidth: 0, fontFamily: 'var(--font-display)', fontSize: 'var(--font-size-md)', fontWeight: 700, color: 'var(--neutral-800)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {findNavItem(active)?.label || 'Admin'}
              </div>
              {here > 0 && (
                <span style={{ flexShrink: 0, fontSize: 'var(--font-size-tiny)', fontWeight: 700, padding: '2px 8px', borderRadius: 'var(--radius-md)', background: 'var(--feedback-error)', color: 'var(--brand-white)' }}>{here} here</span>
              )}
            </div>
          )}
          <div style={{ flex: 1, overflowY: 'auto', paddingBottom: isMobile ? 84 : 0 }}>
            {renderSection()}
          </div>
        </div>
        {/* Narrow screens: the bottom dock. The panel-wide total rides on More, because the menu is where its breakdown lives. */}
        {isMobile && (
          <nav aria-label="Admin" style={{ position: 'fixed', left: 0, right: 0, bottom: 0, zIndex: 250, background: 'var(--brand-white)', borderTop: '1px solid var(--brand-sand)', padding: '6px 4px max(8px, env(safe-area-inset-bottom))', display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)' }}>
            {DOCK.map(d => {
              const isMenu = d.id === MENU_ID;
              const on = isMenu ? sidebarOpen : active === d.id;
              const n = isMenu ? total : (d.badgeKey ? (badges[d.badgeKey] || 0) : 0);
              // On More, an incomplete total shows "N+" (or "!" when nothing loaded).
              const incomplete = isMenu && totalIncomplete;
              const badgeText = incomplete ? (n > 0 ? `${n}+` : '!') : n;
              return (
                <button
                  key={d.id} onClick={() => (isMenu ? setSidebarOpen(true) : handleNav(d.id))}
                  aria-label={incomplete ? `${d.label}, some counts could not load` : (n > 0 ? `${d.label}, ${n} waiting` : d.label)}
                  style={{ position: 'relative', background: 'none', border: 'none', cursor: 'pointer', padding: '7px 2px', minHeight: 54, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3, fontFamily: 'var(--font-body)' }}
                >
                  <span style={{ fontSize: 'var(--font-size-lg)', lineHeight: 1, opacity: on ? 1 : 0.6 }}>{d.icon}</span>
                  <span style={{ fontSize: 'var(--font-size-tiny)', fontWeight: on ? 700 : 500, color: on ? 'var(--brand-rust)' : 'var(--neutral-400)' }}>{d.label}</span>
                  {(n > 0 || incomplete) && (
                    <span style={{ position: 'absolute', top: 3, right: '50%', marginRight: -22, minWidth: 16, height: 16, padding: '0 4px', borderRadius: 8, background: incomplete ? 'var(--feedback-warning-bg)' : 'var(--feedback-error)', color: incomplete ? 'var(--feedback-warning-ink)' : 'var(--brand-white)', fontSize: 'var(--font-size-tiny)', fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{badgeText}</span>
                  )}
                </button>
              );
            })}
          </nav>
        )}
      </div>
    </AdminGuard>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// INLINE SECTIONS — lightweight new sections that don't yet have their own file
// Move each to its own file in /admin/sections/ when it grows.
// ─────────────────────────────────────────────────────────────────────────────

// ── Quotes ────────────────────────────────────────────────────────────────────
function QuotesSection() {
  const [quotes,  setQuotes]  = useState([]);
  const [loading, setLoading] = useState(true);
  const [search,  setSearch]  = useState('');
  const [tab,     setTab]     = useTabParam('tab', 'pending')
  const [page,    setPage]    = useState(0);
  const PER = 10;

  useEffect(() => {
    // Paged: the heading prints quotes.length, so .limit(500) would have
    // shown the page size as the total the moment there were more than 500.
    fetchAllRows(() => supabase
      .from('quote_submissions')
      .select('id, quote_text, book_title_text, author_name_text, submitted_by_username, status, admin_notes, created_at'))
      .then(rows => {
        rows.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
        setQuotes(rows);
        setLoading(false);
      })
      .catch(err => { console.error('QuotesSection error:', err?.message || err); setLoading(false); });
  }, []);

  async function approveQuote(id) {
    const { error } = await supabase
      .from('quote_submissions')
      .update({ status: 'approved', updated_at: new Date().toISOString() })
      .eq('id', id);
    if (!error) setQuotes(q => q.map(x => x.id === id ? { ...x, status: 'approved' } : x));
  }

  async function toggleFlag(id, currentStatus) {
    const newStatus = currentStatus === 'flagged' ? 'approved' : 'flagged';
    const { error } = await supabase
      .from('quote_submissions')
      .update({ status: newStatus, updated_at: new Date().toISOString() })
      .eq('id', id);
    if (!error) setQuotes(q => q.map(x => x.id === id ? { ...x, status: newStatus } : x));
  }

  async function deleteQuote(id) {
    const { error } = await supabase.from('quote_submissions').delete().eq('id', id);
    if (!error) setQuotes(q => q.filter(x => x.id !== id));
  }

  const TABS = [
    { id: 'pending',  label: 'Pending',  fn: q => q.status === 'pending'  },
    { id: 'approved', label: 'Approved', fn: q => q.status === 'approved' },
    { id: 'flagged',  label: 'Flagged',  fn: q => q.status === 'flagged'  },
    { id: 'all',      label: 'All',      fn: () => true                   },
  ];
  const activeTab = TABS.find(t => t.id === tab) || TABS[0];

  const filtered = quotes
    .filter(activeTab.fn)
    .filter(q =>
      (q.quote_text || '').toLowerCase().includes(search.toLowerCase()) ||
      (q.book_title_text || '').toLowerCase().includes(search.toLowerCase()) ||
      (q.submitted_by_username || '').toLowerCase().includes(search.toLowerCase())
    );
  const pages = Math.max(1, Math.ceil(filtered.length / PER));
  const cur   = Math.min(page, pages - 1);
  const slice = filtered.slice(cur * PER, cur * PER + PER);

  return (
    <div style={{ padding: 'clamp(16px, 3vw, 36px)', fontFamily: 'var(--font-body)' }}>
      <div style={{ marginBottom: 18 }}>
        <h1 style={{ fontFamily: 'var(--font-display)', fontSize: 'clamp(20px, 4vw, 24px)', fontWeight: 700, color: 'var(--neutral-800)', margin: 'var(--space-none)' }}>Quotes <span style={{ fontSize: 'var(--font-size-md)', color: 'var(--neutral-400)', fontWeight: 400 }}>· {quotes.length}</span></h1>
        <p style={{ fontSize: 'var(--font-size-base)', color: '#8b7355', margin: '4px 0 0' }}>Review pending quotes, flag or delete what doesn't belong.</p>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 'var(--space-none)', borderBottom: '2px solid #ece3d4', marginBottom: 'var(--space-lg)', overflowX: 'auto' }}>
        {TABS.map(t => {
          const count = quotes.filter(t.fn).length;
          const isActive = tab === t.id;
          return (
            <button key={t.id} onClick={() => { setTab(t.id); setPage(0); }} style={{ padding: '9px 16px', background: 'none', border: 'none', borderBottom: isActive ? '2px solid var(--neutral-800)' : '2px solid transparent', marginBottom: -2, cursor: 'pointer', fontSize: 'var(--font-size-base)', fontWeight: isActive ? 700 : 500, color: isActive ? 'var(--neutral-800)' : 'var(--neutral-400)', whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: 'var(--space-6)' }}>
              {t.label}
              {count > 0 && <span style={{ fontSize: 'var(--font-size-tiny)', fontWeight: 700, padding: '1px 7px', borderRadius: 'var(--radius-md)', background: isActive ? (t.id === 'pending' ? 'var(--brand-rust)' : 'var(--neutral-800)') : 'var(--brand-sand)', color: isActive ? 'var(--brand-white)' : 'var(--neutral-400)' }}>{count}</span>}
            </button>
          );
        })}
      </div>

      <input value={search} onChange={e => { setSearch(e.target.value); setPage(0); }} placeholder="Search quotes, books, or users..."
        style={{ width: '100%', maxWidth: 500, padding: '9px 14px', border: '1px solid var(--brand-sand)', borderRadius: 'var(--radius-10)', fontSize: 'var(--font-size-base)', outline: 'none', marginBottom: 'var(--space-14)', fontFamily: 'var(--font-body)' }} />
      {loading ? (
        <div style={{ padding: 'var(--space-h4)', textAlign: 'center', color: 'var(--neutral-400)', fontSize: 'var(--font-size-base)' }}>Loading quotes...</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-sm)' }}>
          {slice.map(q => (
            <div key={q.id} style={{ background: q.status === 'flagged' ? '#fffaf7' : 'var(--brand-white)', borderRadius: 'var(--radius-lg)', border: q.status === 'flagged' ? '1px solid #fecaca' : '1px solid #ece3d4', padding: '14px 16px', display: 'flex', gap: 'var(--space-14)', alignItems: 'flex-start' }}>
              <span style={{ fontFamily: 'var(--font-display)', fontSize: 'var(--font-size-h3)', color: '#e8dcc8', lineHeight: 0.6, flexShrink: 0 }}>&ldquo;</span>
              <div style={{ flex: 1 }}>
                <p style={{ fontFamily: 'var(--font-display)', fontSize: 'var(--font-size-md)', fontStyle: 'italic', color: 'var(--neutral-700)', margin: '0 0 4px', lineHeight: 1.4 }}>{q.quote_text}</p>
                <p style={{ fontSize: 'var(--font-size-xxs)', color: 'var(--neutral-400)', margin: 'var(--space-none)' }}>
                  {q.book_title_text} · {q.author_name_text} · submitted by {q.submitted_by_username || '—'}
                  {q.status === 'flagged' && <span style={{ color: 'var(--feedback-error)', fontWeight: 700, marginLeft: 'var(--space-6)' }}>⚑ flagged</span>}
                </p>
              </div>
              <div style={{ display: 'flex', gap: 'var(--space-6)', flexShrink: 0, flexWrap: 'wrap' }}>
                {q.status === 'pending' && (
                  <button onClick={() => approveQuote(q.id)} style={{ padding: '6px 12px', background: 'var(--brand-forest)', color: 'var(--brand-white)', border: 'none', borderRadius: 9, fontSize: 'var(--font-size-xs)', fontWeight: 600, cursor: 'pointer' }}>
                    Approve
                  </button>
                )}
                <button onClick={() => toggleFlag(q.id, q.status)} style={{ padding: '6px 12px', background: 'var(--brand-white)', color: 'var(--feedback-warning-ink)', border: '1px solid var(--feedback-warning-border)', borderRadius: 9, fontSize: 'var(--font-size-xs)', fontWeight: 600, cursor: 'pointer' }}>
                  {q.status === 'flagged' ? 'Unflag' : 'Flag'}
                </button>
                <button onClick={() => deleteQuote(q.id)} style={{ padding: '6px 12px', background: 'var(--brand-white)', color: 'var(--feedback-error)', border: '1px solid #fecaca', borderRadius: 9, fontSize: 'var(--font-size-xs)', fontWeight: 600, cursor: 'pointer' }}>Delete</button>
              </div>
            </div>
          ))}
          {slice.length === 0 && <div style={{ padding: 'var(--space-h4)', textAlign: 'center', color: 'var(--neutral-400)', fontSize: 'var(--font-size-base)', background: 'var(--brand-white)', borderRadius: 'var(--radius-lg)', border: '1px solid #ece3d4' }}>No quotes match.</div>}
        </div>
      )}
      {filtered.length > PER && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 'var(--space-10)' }}>
          <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--neutral-400)' }}>Showing {cur * PER + 1}–{Math.min(cur * PER + PER, filtered.length)} of {filtered.length}</span>
          <div style={{ display: 'flex', gap: 'var(--space-6)' }}>
            <button onClick={() => setPage(Math.max(0, cur - 1))} disabled={cur === 0} style={{ padding: '5px 12px', borderRadius: 'var(--radius-md)', border: '1px solid var(--brand-sand)', background: 'var(--brand-white)', color: cur === 0 ? 'var(--neutral-300)' : 'var(--neutral-600)', fontSize: 'var(--font-size-xs)', fontWeight: 600, cursor: cur === 0 ? 'default' : 'pointer' }}>← Prev</button>
            <span style={{ fontSize: 'var(--font-size-xs)', color: '#8b7355', alignSelf: 'center' }}>{cur + 1}/{pages}</span>
            <button onClick={() => setPage(Math.min(pages - 1, cur + 1))} disabled={cur >= pages - 1} style={{ padding: '5px 12px', borderRadius: 'var(--radius-md)', border: '1px solid var(--brand-sand)', background: 'var(--brand-white)', color: cur >= pages - 1 ? 'var(--neutral-300)' : 'var(--neutral-600)', fontSize: 'var(--font-size-xs)', fontWeight: 600, cursor: cur >= pages - 1 ? 'default' : 'pointer' }}>Next →</button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Giveaway ──────────────────────────────────────────────────────────────────
function GiveawaySection() {
  const [giveaway, setGiveaway] = useState(null);
  const [loading,  setLoading]  = useState(true);
  const [editing,  setEditing]  = useState(false);
  const [form,     setForm]     = useState({});
  const [saving,   setSaving]   = useState(false);
  const [saveError, setSaveError] = useState('');
  const [loadError, setLoadError] = useState('');

  useEffect(() => {
    // Newest active giveaway. A bare .maybeSingle() errors the moment two are
    // active at once, and the screen then showed "No active giveaway".
    supabase.from('giveaways').select('*').eq('is_active', true)
      .order('created_at', { ascending: false }).limit(1).maybeSingle()
      .then(({ data, error }) => {
        if (error) setLoadError(error.message);
        setGiveaway(data); if (data) setForm(data); setLoading(false);
      });
  }, []);

  // The editor closes only once the database has said yes. It used to close
  // whatever happened, so a refused save looked like a saved one, and a first
  // save never learned its own id (so the next save inserted a second row).
  async function save() {
    if (saving) return;
    setSaving(true);
    setSaveError('');
    const now = new Date().toISOString();
    let saved = null;
    if (giveaway?.id) {
      const { id: _id, created_at: _created, ...fields } = form;
      const { data: rows, error } = await supabase.from('giveaways')
        .update({ ...fields, updated_at: now }).eq('id', giveaway.id).select('*');
      if (error) { setSaveError(`Not saved: ${error.message}`); setSaving(false); return; }
      if (!rows?.length) { setSaveError('Not saved: the update changed no row (blocked by a policy, or the giveaway no longer exists).'); setSaving(false); return; }
      saved = rows[0];
    } else {
      const { data: row, error } = await supabase.from('giveaways')
        .insert({ ...form, is_active: true, created_at: now }).select('*').single();
      if (error) { setSaveError(`Not saved: ${error.message}`); setSaving(false); return; }
      saved = row;
    }
    setGiveaway(saved);
    setForm(saved);
    setSaving(false);
    setEditing(false);
  }

  if (loading) return <div style={{ padding: 'var(--space-h4)', textAlign: 'center', color: 'var(--neutral-400)' }}>Loading...</div>;

  return (
    <div style={{ padding: 'clamp(16px, 3vw, 36px)', fontFamily: 'var(--font-body)' }}>
      <div style={{ marginBottom: 18 }}>
        <h1 style={{ fontFamily: 'var(--font-display)', fontSize: 'clamp(20px, 4vw, 24px)', fontWeight: 700, color: 'var(--neutral-800)', margin: 'var(--space-none)' }}>Giveaway</h1>
        <p style={{ fontSize: 'var(--font-size-base)', color: '#8b7355', margin: '4px 0 0' }}>Edit the current active giveaway.</p>
      </div>
      <div style={{ background: 'var(--brand-white)', borderRadius: 14, border: '1px solid #ece3d4', padding: 'var(--space-xl)', maxWidth: 600 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: editing ? 16 : 0 }}>
          <p style={{ fontSize: 'var(--font-size-xs)', fontWeight: 700, color: '#8b7355', letterSpacing: 1, textTransform: 'uppercase', margin: 'var(--space-none)' }}>Current giveaway</p>
          <button onClick={() => { if (editing) save(); else { setSaveError(''); setEditing(true); } }} disabled={saving}
            style={{ padding: '7px 16px', background: editing ? 'var(--brand-forest)' : 'var(--brand-white)', color: editing ? 'var(--brand-white)' : 'var(--neutral-600)', border: editing ? 'none' : '1px solid var(--brand-sand)', borderRadius: 9, fontSize: 'var(--font-size-xs)', fontWeight: 600, cursor: 'pointer' }}>
            {editing ? (saving ? 'Saving…' : 'Save changes') : 'Edit'}
          </button>
        </div>
        {loadError && <p role="alert" style={{ fontSize: 'var(--font-size-sm)', color: 'var(--feedback-error)', background: 'var(--feedback-error-bg)', borderRadius: 'var(--radius-md)', padding: '8px 12px', margin: '12px 0 0' }}>Could not load the giveaway: {loadError}</p>}
        {saveError && <p role="alert" style={{ fontSize: 'var(--font-size-sm)', color: 'var(--feedback-error)', background: 'var(--feedback-error-bg)', borderRadius: 'var(--radius-md)', padding: '8px 12px', margin: '12px 0 0' }}>{saveError}</p>}
        {!giveaway && !editing ? (
          <div style={{ padding: '40px 0', textAlign: 'center', color: 'var(--neutral-400)' }}>
            <p style={{ fontSize: 'var(--font-size-base)', margin: '0 0 12px' }}>No active giveaway.</p>
            <button onClick={() => setEditing(true)} style={{ padding: '9px 18px', background: 'var(--neutral-800)', color: 'var(--brand-white)', border: 'none', borderRadius: 'var(--radius-10)', fontSize: 'var(--font-size-sm)', fontWeight: 700, cursor: 'pointer' }}>Create giveaway</button>
          </div>
        ) : editing ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-md)', marginTop: 'var(--space-lg)' }}>
            {[['name', 'Giveaway name'], ['prize', 'Prize'], ['rule', 'How to enter'], ['ends_at', 'End date']].map(([key, label]) => (
              <label key={key}>
                <p style={{ fontSize: 'var(--font-size-xxs)', fontWeight: 600, color: 'var(--neutral-600)', margin: '0 0 4px' }}>{label}</p>
                <input type={key === 'ends_at' ? 'date' : 'text'} value={form[key] || ''} onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))}
                  style={{ width: '100%', padding: '9px 12px', border: '1px solid var(--brand-sand)', borderRadius: 9, fontSize: 'var(--font-size-base)', outline: 'none', boxSizing: 'border-box', fontFamily: 'var(--font-body)' }} />
              </label>
            ))}
          </div>
        ) : (
          <div style={{ marginTop: 'var(--space-14)' }}>
            <p style={{ fontFamily: 'var(--font-display)', fontSize: 'var(--font-size-xl)', fontWeight: 700, color: 'var(--neutral-800)', margin: '0 0 4px' }}>{giveaway.name}</p>
            {giveaway.prize && <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--neutral-600)', margin: '0 0 2px' }}><strong>Prize:</strong> {giveaway.prize}</p>}
            {giveaway.rule && <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--neutral-600)', margin: '0 0 2px' }}><strong>To enter:</strong> {giveaway.rule}</p>}
            {giveaway.ends_at && <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--neutral-600)', margin: 'var(--space-none)' }}><strong>Ends:</strong> {new Date(giveaway.ends_at).toLocaleDateString()}</p>}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Legal Pages ────────────────────────────────────────────────────────────────
function LegalPagesSection() {
  const [tab,      setTab]      = useTabParam('tab', 'tos')
  const [docs,     setDocs]     = useState({});
  const [loading,  setLoading]  = useState(true);
  const [editing,  setEditing]  = useState(false);
  const [draft,    setDraft]    = useState('');
  const [saving,   setSaving]   = useState(false);
  const [saveError, setSaveError] = useState('');
  const today = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });

  useEffect(() => {
    // Two fixed keys, so this cannot exceed the cap — paged anyway so no
    // read in the admin panel is left relying on that staying true.
    fetchAllRows(() => supabase.from('legal_documents').select('key, body, updated_at')
      .in('key', ['tos', 'privacy']), { orderColumn: 'key' })
      .then((data) => {
        const map = {};
        (data || []).forEach(d => { map[d.key] = d; });
        setDocs(map);
        setLoading(false);
      });
  }, []);

  // These pages are the public Terms and Privacy Policy. The editor used to
  // close, and the preview to show the new text, whether or not the database
  // took the change -- so an admin could believe a legal page was published
  // when it was not. It now closes only when the row came back.
  async function save() {
    if (saving) return;
    setSaving(true);
    setSaveError('');
    const existing = docs[tab];
    const updated_at = new Date().toISOString();
    if (existing) {
      const { data: rows, error } = await supabase.from('legal_documents')
        .update({ body: draft, updated_at }).eq('key', tab).select('key');
      if (error) { setSaveError(`Not saved: ${error.message}`); setSaving(false); return; }
      if (!rows?.length) { setSaveError('Not saved: the update changed no row (blocked by a policy, or the page no longer exists).'); setSaving(false); return; }
    } else {
      const { data: row, error } = await supabase.from('legal_documents')
        .insert({ key: tab, body: draft, updated_at }).select('key').single();
      if (error) { setSaveError(`Not saved: ${error.message}`); setSaving(false); return; }
      if (!row) { setSaveError('Not saved: the database returned no row.'); setSaving(false); return; }
    }
    setDocs(d => ({ ...d, [tab]: { key: tab, body: draft, updated_at } }));
    setSaving(false);
    setEditing(false);
  }

  const doc = docs[tab];

  // Marker lines (`<a id="..."></a>` alone on a line) are how an editor pins
  // a URL-stable id onto a section -- see PrivacyPolicy.jsx's ANCHOR_LINE_RE
  // for the full reasoning. They render as nothing here too, matching the
  // public page, rather than showing the raw tag as text and making this
  // preview look broken for something that isn't.
  const ANCHOR_LINE_RE = /^\s*<a\s+id=["'\u201C\u201D\u2018\u2019][a-z0-9-]+["'\u201C\u201D\u2018\u2019]\s*\/?\s*>(?:\s*<\/a>)?\s*$/i;

  // Simple markdown renderer
  const renderMd = (md = '') => md.split('\n').map((line, i) => {
    if (ANCHOR_LINE_RE.test(line))  return null;
    if (line.startsWith('## ')) return <p key={i} style={{ fontFamily: 'var(--font-display)', fontSize: 'var(--font-size-md)', fontWeight: 700, color: 'var(--neutral-800)', margin: '12px 0 4px' }}>{line.slice(3)}</p>;
    if (line.startsWith('# '))  return <p key={i} style={{ fontFamily: 'var(--font-display)', fontSize: 'var(--font-size-xl)', fontWeight: 700, color: 'var(--neutral-800)', margin: '4px 0 8px' }}>{line.slice(2)}</p>;
    if (line.startsWith('- '))  return <p key={i} style={{ fontSize: 'var(--font-size-sm)', color: 'var(--neutral-600)', margin: '2px 0 2px 16px' }}>• {line.slice(2)}</p>;
    if (line.trim() === '')     return <div key={i} style={{ height: 6 }} />;
    return <p key={i} style={{ fontSize: 'var(--font-size-sm)', color: 'var(--neutral-600)', margin: '0 0 4px', lineHeight: 1.5 }}>{line}</p>;
  });

  if (loading) return <div style={{ padding: 'var(--space-h4)', textAlign: 'center', color: 'var(--neutral-400)' }}>Loading...</div>;

  return (
    <div style={{ padding: 'clamp(16px, 3vw, 36px)', fontFamily: 'var(--font-body)' }}>
      <div style={{ marginBottom: 18 }}>
        <h1 style={{ fontFamily: 'var(--font-display)', fontSize: 'clamp(20px, 4vw, 24px)', fontWeight: 700, color: 'var(--neutral-800)', margin: 'var(--space-none)' }}>Legal Pages</h1>
        <p style={{ fontSize: 'var(--font-size-base)', color: '#8b7355', margin: '4px 0 0' }}>Edit ToS and Privacy Policy. Changes go live on the public site, no developer needed.</p>
      </div>
      <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 'var(--radius-10)', padding: '10px 14px', marginBottom: 'var(--space-lg)', maxWidth: 680 }}>
        <p style={{ fontSize: 'var(--font-size-xs)', color: '#1e40af', margin: 'var(--space-none)', lineHeight: 1.5 }}>Use Markdown: <strong># heading</strong>, <strong>## subheading</strong>, <strong>**bold**</strong>, <strong>- bullet</strong>. Each save stamps a new "last updated" date.</p>
        <p style={{ fontSize: 'var(--font-size-xs)', color: '#1e40af', margin: '6px 0 0', lineHeight: 1.5 }}>To link straight into a section from outside this page (a consent checkbox, a support email), put <strong>{'<a id="your-slug"></a>'}</strong> on its own line directly above the heading. It never shows up on the published page, it just pins that section's web address to "your-slug" even if the heading's wording changes later.</p>
      </div>
      {/* Tabs */}
      <div style={{ display: 'flex', gap: 'var(--space-none)', borderBottom: '2px solid #ece3d4', marginBottom: 'var(--space-lg)' }}>
        {[['tos', 'Terms of Service'], ['privacy', 'Privacy Policy']].map(([k, l]) => (
          <button key={k} onClick={() => { setTab(k); setEditing(false); setSaveError(''); }} style={{ padding: '9px 18px', background: 'none', border: 'none', borderBottom: tab === k ? '2px solid var(--neutral-800)' : '2px solid transparent', marginBottom: -2, cursor: 'pointer', fontSize: 12.5, fontWeight: tab === k ? 700 : 600, color: tab === k ? 'var(--neutral-800)' : 'var(--neutral-400)' }}>{l}</button>
        ))}
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--space-md)', maxWidth: 760 }}>
        <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--neutral-400)' }}>
          {doc?.updated_at ? <>Last updated: <strong style={{ color: 'var(--neutral-600)' }}>{new Date(doc.updated_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</strong></> : 'Not set yet'}
        </span>
        {!editing
          ? <button onClick={() => { setDraft(doc?.body || ''); setSaveError(''); setEditing(true); }} style={{ padding: '7px 16px', background: 'var(--neutral-800)', color: 'var(--brand-white)', border: 'none', borderRadius: 9, fontSize: 'var(--font-size-sm)', fontWeight: 600, cursor: 'pointer' }}>Edit</button>
          : <div style={{ display: 'flex', gap: 'var(--space-sm)' }}>
              <button onClick={() => { setEditing(false); setSaveError(''); }} style={{ padding: '7px 14px', background: 'var(--brand-white)', color: 'var(--neutral-600)', border: '1px solid var(--brand-sand)', borderRadius: 9, fontSize: 'var(--font-size-sm)', fontWeight: 600, cursor: 'pointer' }}>Cancel</button>
              <button onClick={save} disabled={saving} style={{ padding: '7px 16px', background: 'var(--brand-forest)', color: 'var(--brand-white)', border: 'none', borderRadius: 9, fontSize: 'var(--font-size-sm)', fontWeight: 700, cursor: 'pointer' }}>{saving ? 'Saving…' : 'Save & publish'}</button>
            </div>
        }
      </div>
      {saveError && <p role="alert" style={{ fontSize: 'var(--font-size-sm)', color: 'var(--feedback-error)', background: 'var(--feedback-error-bg)', borderRadius: 'var(--radius-md)', padding: '8px 12px', margin: '0 0 12px', maxWidth: 760 }}>{saveError}</p>}
      {editing ? (
        <div style={{ display: 'flex', gap: 'var(--space-14)', maxWidth: 980 }}>
          <div style={{ flex: 1 }}>
            <p style={{ fontSize: 'var(--font-size-xxs)', fontWeight: 700, color: '#8b7355', letterSpacing: 0.5, margin: '0 0 6px' }}>EDIT (MARKDOWN)</p>
            <textarea value={draft} onChange={e => setDraft(e.target.value)}
              style={{ width: '100%', minHeight: 420, padding: '12px 14px', border: '1px solid var(--brand-sand)', borderRadius: 'var(--radius-10)', fontSize: 12.5, lineHeight: 1.6, outline: 'none', fontFamily: 'var(--font-mono)', resize: 'vertical', boxSizing: 'border-box' }} />
          </div>
          <div style={{ flex: 1 }}>
            <p style={{ fontSize: 'var(--font-size-xxs)', fontWeight: 700, color: '#8b7355', letterSpacing: 0.5, margin: '0 0 6px' }}>LIVE PREVIEW</p>
            <div style={{ minHeight: 420, padding: '14px 18px', border: '1px solid #ece3d4', borderRadius: 'var(--radius-10)', background: 'var(--brand-white)', overflowY: 'auto' }}>{renderMd(draft)}</div>
          </div>
        </div>
      ) : (
        <div style={{ padding: '18px 22px', border: '1px solid #ece3d4', borderRadius: 'var(--radius-lg)', background: 'var(--brand-white)', maxWidth: 760 }}>
          {doc?.body ? renderMd(doc.body) : <p style={{ fontSize: 'var(--font-size-base)', color: 'var(--neutral-400)', fontStyle: 'italic' }}>No content yet. Click Edit to add.</p>}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// READERS SECTION — full roster + read-only profile modal
// ─────────────────────────────────────────────────────────────────────────────
function ReadersSection() {
  const [readers,    setReaders]    = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [search,     setSearch]     = useState('');
  const [page,       setPage]       = useState(0);
  const [viewReader, setViewReader] = useState(null);
  const PER = 10;

  useEffect(() => {
    // Paged. This was .limit(500) with the heading printing readers.length,
    // so a table of 864 rendered as "Readers · 500" under a subtitle that
    // says "Every reader account". It was neither every account nor 500 by
    // any meaningful definition — it was the page size, shown as a total.
    fetchAllRows(() => supabase.from('user_profiles')
      .select('user_id, username, display_name, avatar_url, created_at, profile_blocks, interest_tags')
      , { orderColumn: 'user_id' })
      .then(rows => {
        rows.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
        setReaders(rows);
        setLoading(false);
      })
      .catch(err => { console.error('ReadersSection error:', err?.message || err); setLoading(false); });
  }, []);

  const filtered = readers.filter(r =>
    (r.username || '').toLowerCase().includes(search.toLowerCase()) ||
    (r.display_name || '').toLowerCase().includes(search.toLowerCase())
  );
  const pages = Math.max(1, Math.ceil(filtered.length / PER));
  const cur   = Math.min(page, pages - 1);
  const slice = filtered.slice(cur * PER, cur * PER + PER);

  return (
    <div style={{ padding: '30px 36px', fontFamily: 'var(--font-body)' }}>
      <div style={{ marginBottom: 18 }}>
        <h1 style={{ fontFamily: 'var(--font-display)', fontSize: 'var(--font-size-h4)', fontWeight: 700, color: 'var(--neutral-800)', margin: 'var(--space-none)' }}>
          Readers <span style={{ fontSize: 'var(--font-size-md)', color: 'var(--neutral-400)', fontWeight: 400 }}>· {readers.length.toLocaleString()}</span>
        </h1>
        <p style={{ fontSize: 'var(--font-size-base)', color: '#8b7355', margin: '4px 0 0' }}>Every reader account. Enforcement (suspend/ban) lives under Moderation → Users.</p>
      </div>
      <input value={search} onChange={e => { setSearch(e.target.value); setPage(0); }}
        placeholder="Search username or name..."
        style={{ width: '100%', maxWidth: 400, padding: '9px 14px', border: '1px solid var(--brand-sand)', borderRadius: 'var(--radius-10)', fontSize: 'var(--font-size-base)', outline: 'none', marginBottom: 'var(--space-14)', fontFamily: 'var(--font-body)' }} />

      {/* Fixed-height paginated box — 10 per page */}
      <div style={{ border: '1px solid var(--brand-sand)', borderRadius: 14, background: '#fdfbf7', padding: 'var(--space-14)', maxWidth: 780 }}>
        <div style={{ minHeight: 540, display: 'flex', flexDirection: 'column', gap: 'var(--space-none)' }}>
          {loading ? (
            <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--neutral-400)', fontSize: 'var(--font-size-base)' }}>Loading readers...</div>
          ) : slice.length === 0 ? (
            <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--neutral-400)', fontSize: 'var(--font-size-base)', fontStyle: 'italic' }}>
              {search ? `No readers matching "${search}"` : 'No readers yet.'}
            </div>
          ) : slice.map((r, i) => (
            <div key={r.user_id} style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-md)', padding: '12px 4px', borderBottom: i < slice.length - 1 ? '1px solid var(--brand-cream)' : 'none' }}>
              <div style={{ width: 34, height: 34, borderRadius: 'var(--radius-circle)', background: 'linear-gradient(135deg,var(--brand-rust),var(--brand-gold))', color: 'var(--brand-white)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 'var(--font-size-sm)', fontWeight: 700, fontFamily: 'var(--font-display)', flexShrink: 0 }}>
                {(r.username || r.display_name || 'U').slice(0, 1).toUpperCase()}
              </div>
              <div style={{ flex: 1 }}>
                <span style={{ fontSize: 'var(--font-size-base)', fontWeight: 700, color: 'var(--neutral-800)' }}>@{r.username || 'unnamed'}</span>
                {r.display_name && <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--neutral-400)', marginLeft: 'var(--space-sm)' }}>{r.display_name}</span>}
                <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--neutral-400)', marginLeft: 'var(--space-sm)' }}>
                  joined {new Date(r.created_at).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}
                </span>
                {(r.interest_tags || []).length > 0 && (
                  <span style={{ fontSize: 'var(--font-size-xxs)', color: 'var(--neutral-400)', marginLeft: 'var(--space-sm)' }}>{r.interest_tags.join(' · ')}</span>
                )}
              </div>
              <button onClick={() => setViewReader(r)}
                style={{ padding: '5px 12px', background: 'var(--brand-white)', color: 'var(--neutral-600)', border: '1px solid var(--brand-sand)', borderRadius: 'var(--radius-md)', fontSize: 'var(--font-size-xs)', fontWeight: 600, cursor: 'pointer', fontFamily: 'var(--font-body)' }}>
                View profile
              </button>
            </div>
          ))}
        </div>
        {filtered.length > PER && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 'var(--space-md)', paddingTop: 'var(--space-md)', borderTop: '1px solid #ece3d4' }}>
            <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--neutral-400)' }}>Showing {cur * PER + 1}–{Math.min(cur * PER + PER, filtered.length)} of {filtered.length}</span>
            <div style={{ display: 'flex', gap: 'var(--space-6)', alignItems: 'center' }}>
              <button onClick={() => setPage(Math.max(0, cur - 1))} disabled={cur === 0} style={{ padding: '5px 12px', borderRadius: 'var(--radius-md)', border: '1px solid var(--brand-sand)', background: 'var(--brand-white)', color: cur === 0 ? 'var(--neutral-300)' : 'var(--neutral-600)', fontSize: 'var(--font-size-xs)', fontWeight: 600, cursor: cur === 0 ? 'default' : 'pointer' }}>← Prev</button>
              <span style={{ fontSize: 'var(--font-size-xs)', color: '#8b7355' }}>{cur + 1}/{pages}</span>
              <button onClick={() => setPage(Math.min(pages - 1, cur + 1))} disabled={cur >= pages - 1} style={{ padding: '5px 12px', borderRadius: 'var(--radius-md)', border: '1px solid var(--brand-sand)', background: 'var(--brand-white)', color: cur >= pages - 1 ? 'var(--neutral-300)' : 'var(--neutral-600)', fontSize: 'var(--font-size-xs)', fontWeight: 600, cursor: cur >= pages - 1 ? 'default' : 'pointer' }}>Next →</button>
            </div>
          </div>
        )}
      </div>
      <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--neutral-400)', margin: '10px 0 0' }}>Readers manage their own account details from Settings, no edit controls here.</p>

      {/* ── Read-only profile modal ── */}
      {viewReader && <ReaderProfileModal reader={viewReader} onClose={() => setViewReader(null)} />}
    </div>
  );
}

function ReaderProfileModal({ reader, onClose }) {
  const [blocks,  setBlocks]  = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function load() {
      // Profile blocks from user_profiles
      const { data: profile } = await supabase
        .from('user_profiles')
        .select('profile_blocks')
        .eq('user_id', reader.user_id)
        .maybeSingle();
      setBlocks(profile?.profile_blocks || []);

      // Activity log — posts + library adds
      const [{ data: posts }, { data: library }] = await Promise.all([
        supabase.from('posts').select('post_type, content, created_at').eq('user_id', reader.user_id).order('created_at', { ascending: false }).limit(10),
        supabase.from('user_library').select('shelf, date_added').eq('user_id', reader.user_id).order('date_added', { ascending: false }).limit(10),
      ]);

      const events = [
        ...(posts || []).map(p => ({ label: p.post_type === 'text' ? `Posted: "${(p.content || '').slice(0, 60)}…"` : 'Made a post', when: p.created_at })),
        ...(library || []).map(l => ({ label: `Added a book to ${l.shelf}`, when: l.date_added })),
        { label: 'Joined Reanlo', when: reader.created_at },
      ].sort((a, b) => new Date(b.when) - new Date(a.when)).slice(0, 12);

      setHistory(events);
      setLoading(false);
    }
    load();
  }, [reader.user_id]);

  const BLOCK_LABELS = {
    current_thought: { ic: '💭', n: 'Current Thought' },
    currently_reading: { ic: '📖', n: 'Currently Reading' },
    to_know_me: { ic: '💛', n: 'To Know Me' },
    top_tropes: { ic: '🏷️', n: 'My Top Tropes' },
    hot_take: { ic: '🔥', n: 'Hot Take' },
    reading_stats: { ic: '📊', n: 'Reading Stats' },
    reading_goal: { ic: '🎯', n: 'Reading Goal' },
    my_shop: { ic: '🛍️', n: 'My Shop' },
    ask_me_about: { ic: '💼', n: 'Ask Me About' },
    substack: { ic: '✍️', n: 'Substack' },
    events: { ic: '📍', n: 'My Events' },
    showcase: { ic: '🎨', n: 'Showcase' },
    recommend_me: { ic: '📚', n: 'If You Like What I Like' },
    custom_shelf: { ic: '📚', n: 'Custom Shelf' },
    youtube: { ic: '🎬', n: 'My YouTube' },
    podcast: { ic: '🎙️', n: 'My Podcast' },
  };

  function timeAgo(ts) {
    if (!ts) return '—';
    const d = Math.floor((Date.now() - new Date(ts)) / 86400000);
    if (d === 0) return 'today';
    if (d === 1) return 'yesterday';
    if (d < 7)  return `${d}d ago`;
    if (d < 30) return `${Math.floor(d/7)}w ago`;
    return new Date(ts).toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
  }

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 400, background: 'rgba(41,37,36,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 'var(--space-xxl)' }}>
      <div onClick={e => e.stopPropagation()} style={{ background: 'var(--brand-white)', borderRadius: 18, width: '100%', maxWidth: 560, maxHeight: '86vh', overflowY: 'auto', boxShadow: '0 24px 60px rgba(0,0,0,0.3)' }}>
        {/* Header */}
        <div style={{ padding: '20px 24px', borderBottom: '1px solid var(--brand-cream)', display: 'flex', alignItems: 'center', gap: 'var(--space-md)', position: 'sticky', top: 0, background: 'var(--brand-white)', borderRadius: '18px 18px 0 0', zIndex: 10 }}>
          <div style={{ width: 44, height: 44, borderRadius: 'var(--radius-circle)', background: 'linear-gradient(135deg,var(--brand-rust),var(--brand-gold))', color: 'var(--brand-white)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 17, fontWeight: 700, fontFamily: 'var(--font-display)' }}>
            {(reader.username || reader.display_name || 'U').slice(0, 1).toUpperCase()}
          </div>
          <div style={{ flex: 1 }}>
            <p style={{ fontFamily: 'var(--font-display)', fontSize: 17, fontWeight: 700, color: 'var(--neutral-800)', margin: 'var(--space-none)' }}>@{reader.username || 'unnamed'}</p>
            <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--neutral-400)', margin: 'var(--space-none)' }}>
              {reader.display_name && `${reader.display_name} · `}
              joined {new Date(reader.created_at).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}
            </p>
          </div>
          <button onClick={onClose} style={{ width: 30, height: 30, borderRadius: 'var(--radius-circle)', background: 'var(--neutral-100)', border: 'none', fontSize: 15, color: 'var(--neutral-500)', cursor: 'pointer' }}>×</button>
        </div>

        <div style={{ padding: '18px 24px' }}>
          {loading ? (
            <div style={{ padding: 'var(--space-h4)', textAlign: 'center', color: 'var(--neutral-400)', fontSize: 'var(--font-size-base)' }}>Loading profile...</div>
          ) : (
            <>
              {/* Profile blocks */}
              <p style={{ fontSize: 'var(--font-size-xxs)', fontWeight: 700, letterSpacing: 0.5, color: '#8b7355', textTransform: 'uppercase', margin: '0 0 10px' }}>
                Profile blocks · {blocks.length}
              </p>
              {blocks.length === 0 ? (
                <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--neutral-400)', fontStyle: 'italic', marginBottom: 'var(--space-xl)' }}>No blocks added yet.</p>
              ) : (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-sm)', marginBottom: 'var(--space-xxl)' }}>
                  {blocks.map((b, i) => {
                    const def = BLOCK_LABELS[b.type] || { ic: '📦', n: b.type };
                    return (
                      <div key={i} style={{ background: '#fdfbf7', border: '1px solid #ece3d4', borderRadius: 'var(--radius-10)', padding: '10px 12px' }}>
                        <div style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--neutral-800)', marginBottom: 'var(--space-xxs)' }}>{def.ic} {def.n}</div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Activity timeline */}
              <p style={{ fontSize: 'var(--font-size-xxs)', fontWeight: 700, letterSpacing: 0.5, color: '#8b7355', textTransform: 'uppercase', margin: '0 0 10px' }}>Profile history</p>
              <div>
                {history.map((h, i) => (
                  <div key={i} style={{ display: 'flex', gap: 'var(--space-10)', padding: '9px 0', borderBottom: i < history.length - 1 ? '1px solid var(--brand-cream)' : 'none' }}>
                    <div style={{ width: 7, height: 7, borderRadius: 'var(--radius-circle)', background: 'var(--brand-gold)', marginTop: 5, flexShrink: 0 }} />
                    <div style={{ flex: 1 }}><span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--neutral-800)' }}>{h.label}</span></div>
                    <span style={{ fontSize: 10.5, color: 'var(--neutral-400)', whiteSpace: 'nowrap' }}>{timeAgo(h.when)}</span>
                  </div>
                ))}
              </div>

              <p style={{ fontSize: 'var(--font-size-xxs)', color: 'var(--neutral-400)', margin: '16px 0 0', lineHeight: 1.5 }}>
                Read-only view. To suspend or ban this account, go to Moderation → Users.
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// TROPE CATALOG SECTION (Config → Tropes)
// Full canonical trope list with book counts, aliases inline, rename + + alias.
// Merge suggestions and the pending-trope queue live in the Trope Queue
// section (src/admin/sections/TropeQueueSection.jsx), not here. This is the
// catalog: browsing and editing tropes that already exist.
// ─────────────────────────────────────────────────────────────────────────────
function TropeCatalogSection({ onNavigate }) {
  const [tropes,  setTropes]  = useState([]);
  const [loading, setLoading] = useState(true);
  const [search,  setSearch]  = useState('');
  const [page,    setPage]    = useState(0);
  const [pendingMerges, setPendingMerges] = useState(0);
  const PER = 10;

  useEffect(() => {
    async function load() {
      // Fetch active tropes with their aliases
      // Paged. There is no .limit() here, but the response still caps at
      // 1,000 rows, and the heading prints tropes.length as "N canonical" —
      // so past 1,000 tropes the catalogue would quietly report exactly 1,000
      // for ever, which is the least obvious way for a number to be wrong.
      let data;
      try {
        data = await fetchAllRows(() => supabase
          .from('tropes')
          .select('id, canonical_name, category, description, usage_count, merge_status, merged_into, created_at, aliases:trope_aliases(id, alias)')
          .eq('is_active', true));
        data.sort((a, b) => (a.canonical_name || '').localeCompare(b.canonical_name || ''));
      } catch (err) {
        console.error('TropeCatalog error:', err?.message || err); setLoading(false); return;
      }

      // Count pending merges — tropes waiting to be reviewed
      const { count } = await supabase
        .from('tropes')
        .select('*', { count: 'exact', head: true })
        .eq('is_active', false)
        .eq('merge_status', 'pending_review');

      setTropes(data || []);
      setPendingMerges(count || 0);
      setLoading(false);
    }
    load();
  }, []);

  const filtered = tropes.filter(t =>
    (t.canonical_name || '').toLowerCase().includes(search.toLowerCase())
  );
  const pages = Math.max(1, Math.ceil(filtered.length / PER));
  const cur   = Math.min(page, pages - 1);
  const slice = filtered.slice(cur * PER, cur * PER + PER);

  async function handleRename(trope) {
    const newName = prompt(`Rename "${trope.canonical_name}" to:`, trope.canonical_name);
    if (!newName || newName.trim() === trope.canonical_name) return;
    await supabase.from('tropes').update({ canonical_name: newName.trim() }).eq('id', trope.id);
    setTropes(t => t.map(x => x.id === trope.id ? { ...x, canonical_name: newName.trim() } : x));
  }

  return (
    <div style={{ padding: 'clamp(16px, 3vw, 36px)', fontFamily: 'var(--font-body)' }}>
      <div style={{ marginBottom: 18 }}>
        <h1 style={{ fontFamily: 'var(--font-display)', fontSize: 'clamp(20px, 4vw, 24px)', fontWeight: 700, color: 'var(--neutral-800)', margin: 'var(--space-none)' }}>Tropes <span style={{ fontSize: 'var(--font-size-md)', color: 'var(--neutral-400)', fontWeight: 400 }}>· {tropes.length} canonical</span></h1>
        <p style={{ fontSize: 'var(--font-size-base)', color: '#8b7355', margin: '4px 0 0' }}>Every canonical trope on Reanlo. Aliases show inline. Rename updates everywhere at once.</p>
      </div>

      {/* Pending merges banner */}
      {pendingMerges > 0 && (
        <div style={{ background: '#f3e8ff', border: '1px solid #d8b4fe', borderRadius: 'var(--radius-10)', padding: '10px 14px', marginBottom: 'var(--space-14)', maxWidth: 640, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <p style={{ fontSize: 'var(--font-size-xs)', color: '#6b21a8', margin: 'var(--space-none)' }}>{pendingMerges} merge suggestion{pendingMerges !== 1 ? 's' : ''} waiting for your decision.</p>
          <button onClick={() => onNavigate?.('c-trope-queue')} style={{ fontSize: 'var(--font-size-xs)', fontWeight: 700, color: '#7c3aed', background: 'var(--brand-white)', border: '1px solid #d8b4fe', borderRadius: 'var(--radius-md)', padding: '5px 12px', cursor: 'pointer' }}>
            Review in queue →
          </button>
        </div>
      )}

      <div style={{ background: '#fffbeb', border: '1px solid var(--feedback-warning-border)', borderRadius: 'var(--radius-10)', padding: '10px 14px', marginBottom: 'var(--space-14)', maxWidth: 640 }}>
        <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--feedback-warning-ink)', margin: 'var(--space-none)', lineHeight: 1.5 }}>
          Typos & formatting (caps, dashes, "2" vs "to") are normalized silently and never appear here. This list is only canonical tropes and the meaningful aliases approved via merges.
        </p>
      </div>

      <div style={{ display: 'flex', gap: 'var(--space-sm)', marginBottom: 'var(--space-14)', maxWidth: 640 }}>
        <input value={search} onChange={e => { setSearch(e.target.value); setPage(0); }} placeholder="Search tropes..."
          style={{ flex: 1, padding: '9px 14px', border: '1px solid var(--brand-sand)', borderRadius: 'var(--radius-10)', fontSize: 'var(--font-size-base)', outline: 'none', fontFamily: 'var(--font-body)' }} />
      </div>

      {loading ? (
        <div style={{ padding: 'var(--space-h4)', textAlign: 'center', color: 'var(--neutral-400)', fontSize: 'var(--font-size-base)' }}>Loading tropes...</div>
      ) : (
        <>
          <div style={{ border: '1px solid #ece3d4', borderRadius: 14, background: '#fdfbf7', padding: 'var(--space-14)', maxWidth: 640 }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-sm)', minHeight: 500 }}>
              {slice.length === 0 && <div style={{ padding: 'var(--space-h4)', textAlign: 'center', color: 'var(--neutral-400)', fontSize: 'var(--font-size-base)' }}>No tropes match.</div>}
              {slice.map(t => {
                const aliases = (t.aliases || []).map(a => a.alias).filter(Boolean);
                const bookCount = t.usage_count || 0;
                return (
                  <div key={t.id} style={{ background: 'var(--brand-white)', borderRadius: 'var(--radius-lg)', border: '1px solid #ece3d4', padding: '13px 16px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-10)' }}>
                      <div style={{ flex: 1 }}>
                        <span style={{ fontSize: 'var(--font-size-md)', fontWeight: 700, color: 'var(--neutral-800)' }}>{t.canonical_name}</span>
                        <span style={{ fontSize: 'var(--font-size-xxs)', color: 'var(--neutral-400)', marginLeft: 'var(--space-sm)' }}>
                          {bookCount > 0 ? `${bookCount} books` : ''}
                          {aliases.length > 0 ? ` · ${aliases.length} alias${aliases.length > 1 ? 'es' : ''}` : ''}
                        </span>
                      </div>
                      <button onClick={() => handleRename(t)} style={{ padding: '5px 12px', background: 'var(--brand-white)', color: 'var(--neutral-600)', border: '1px solid var(--brand-sand)', borderRadius: 'var(--radius-md)', fontSize: 10.5, fontWeight: 600, cursor: 'pointer' }}>
                        Rename
                      </button>
                    </div>
                    {aliases.length > 0 && (
                      <div style={{ display: 'flex', gap: 'var(--space-6)', flexWrap: 'wrap', marginTop: 'var(--space-10)', paddingTop: 'var(--space-10)', borderTop: '1px solid var(--brand-cream)' }}>
                        <span style={{ fontSize: 'var(--font-size-tiny)', fontWeight: 700, color: 'var(--neutral-400)', letterSpacing: 0.5, alignSelf: 'center' }}>ALIASES:</span>
                        {aliases.map(a => (
                          <span key={a} style={{ fontSize: 'var(--font-size-xs)', padding: '3px 9px', borderRadius: 11, background: 'var(--brand-paper)', color: 'var(--neutral-600)', border: '1px solid var(--brand-sand)' }}>{a}</span>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            {filtered.length > PER && (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 'var(--space-md)', paddingTop: 'var(--space-md)', borderTop: '1px solid #ece3d4' }}>
                <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--neutral-400)' }}>Showing {cur * PER + 1}–{Math.min(cur * PER + PER, filtered.length)} of {filtered.length}</span>
                <div style={{ display: 'flex', gap: 'var(--space-6)', alignItems: 'center' }}>
                  <button onClick={() => setPage(Math.max(0, cur - 1))} disabled={cur === 0} style={{ padding: '5px 12px', borderRadius: 'var(--radius-md)', border: '1px solid var(--brand-sand)', background: 'var(--brand-white)', color: cur === 0 ? 'var(--neutral-300)' : 'var(--neutral-600)', fontSize: 'var(--font-size-xs)', fontWeight: 600, cursor: cur === 0 ? 'default' : 'pointer' }}>← Prev</button>
                  <span style={{ fontSize: 'var(--font-size-xs)', color: '#8b7355' }}>{cur + 1}/{pages}</span>
                  <button onClick={() => setPage(Math.min(pages - 1, cur + 1))} disabled={cur >= pages - 1} style={{ padding: '5px 12px', borderRadius: 'var(--radius-md)', border: '1px solid var(--brand-sand)', background: 'var(--brand-white)', color: cur >= pages - 1 ? 'var(--neutral-300)' : 'var(--neutral-600)', fontSize: 'var(--font-size-xs)', fontWeight: 600, cursor: cur >= pages - 1 ? 'default' : 'pointer' }}>Next →</button>
                </div>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

function CompClaimsSection() {
  const [comps,      setComps]      = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [compSearch, setCompSearch] = useState('');
  const [compPage,   setCompPage]   = useState(0);
  const PER = 6;
 
  useEffect(() => {
    async function load() {
      try {
        // Comps — stored as comp_titles[] on the books table
        // Each entry is an object: { title, author, cover, shared }
        const booksWithComps = await fetchAllRows(() => supabase
          .from('books')
          .select('id, title, author_name, comp_titles, created_at')
          .not('comp_titles', 'is', null));
        booksWithComps.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
 
        // Flatten into one row per comp claim
        const compRows = [];
        (booksWithComps || []).forEach(b => {
          (b.comp_titles || []).filter(Boolean).forEach((comp, i) => {
            const compTitle = typeof comp === 'string' ? comp : comp.title;
            const compAuthor = typeof comp === 'object' ? comp.author : null;
            const compCover  = typeof comp === 'object' ? comp.cover  : null;
            const sharedTags = typeof comp === 'object' ? (comp.shared || []) : [];
            compRows.push({
              id:          `${b.id}-${i}`,
              comp_index:  i,
              book_id:     b.id,
              book_title:  b.title,
              author_name: b.author_name,
              comp_title:  compTitle || '—',
              comp_author: compAuthor,
              comp_cover:  compCover,
              shared_tags: sharedTags,
              _raw:        comp,
            });
          });
        });
        setComps(compRows);
      } catch (err) {
        console.error('CompClaimsSection error:', err.message);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);
 
  // Remove takes the comp off the book AND hides the pipeline the author's
  // save made from it. It used to do only the first, so the approved pipeline
  // stayed on the homepage until the author next saved the book.
  //
  // Order: the claim first, the pipeline second. If the second step fails the
  // admin is told which half worked, because "removed" alone would be a lie.
  async function removeComp(compRow) {
    if (!confirm('Remove this comp claim? Its pipeline on the homepage will be hidden too.')) return;

    const { data: book, error: readErr } = await supabase.from('books')
      .select('id, title, author_name, comp_titles').eq('id', compRow.book_id).single();
    if (readErr || !book) { alert(`Comp claim NOT removed: could not read the book${readErr ? ` (${readErr.message})` : ''}.`); return; }

    // compRow.comp_index counts the non-empty entries only (the list is built
    // from .filter(Boolean)), so count the same way here. Filtering the raw
    // array by that index removed the wrong comp whenever a blank entry came
    // first.
    const live = (book.comp_titles || []).filter(Boolean);
    const target = live[compRow.comp_index];
    const targetTitle = typeof target === 'string' ? target : target?.title;
    if (!target || (targetTitle || '—') !== compRow.comp_title) {
      alert('Comp claim NOT removed: this book\'s comps changed since the page loaded. Reload and try again.');
      return;
    }
    const updated = live.filter((_, i) => i !== compRow.comp_index);

    const { data: saved, error } = await supabase.from('books')
      .update({ comp_titles: updated }).eq('id', compRow.book_id).select('id');
    if (error || !saved?.length) {
      alert(`Comp claim NOT removed${error ? `: ${error.message}` : ': the update changed no row (blocked by a policy, or the book no longer exists)'}. It is still on the book.`);
      return;
    }

    // Match on the book's id, not its title: two books can share a title and a
    // pen name, and a renamed book keeps its id. Rows linked to no book
    // (gem_book_id null) are left alone, as the author app leaves them when it
    // can't tell whose a row is; an unlinked pipeline can't reach the homepage
    // anyway. Only pending/approved rows are touched: rejected and hidden are
    // admin decisions already.
    const esc = (v) => String(v).replace(/[\\%_]/g, '\\$&');
    const compAuthor = typeof target === 'object' ? target.author : null;
    const { data: hidden, error: hideErr } = await withPopAuthor(
      supabase.from('pipelines').update({ status: 'hidden' })
        .eq('entry_point', 'author-comp')
        .eq('gem_book_id', book.id)
        .in('status', ['pending', 'approved'])
        .ilike('pop_book_title', esc(targetTitle)),
      compAuthor, esc,
    ).select('id');

    // The claim is gone from the book, so take it off the list and shift the
    // numbering of this book's later comps (ids are built from the position).
    setComps(c => c
      .filter(x => x.id !== compRow.id)
      .map(x => x.book_id === compRow.book_id && x.comp_index > compRow.comp_index
        ? { ...x, comp_index: x.comp_index - 1, id: `${x.book_id}-${x.comp_index - 1}` }
        : x));

    if (hideErr) {
      alert(`Comp claim removed, but its homepage pipeline was NOT hidden: ${hideErr.message}. Hide it from the Pipelines screen.`);
    } else if (!hidden?.length) {
      alert('Comp claim removed. No open pipeline for it was found, so nothing was hidden. Check Pipelines if one is still showing.');
    }
    await logAudit('update', 'book', `Removed comp claim "${targetTitle}" from "${book.title}"${hidden?.length ? ` and hid ${hidden.length} pipeline${hidden.length === 1 ? '' : 's'}` : ''}`, book.id);
  }
 
  const filteredComps = comps.filter(c => JSON.stringify(c).toLowerCase().includes(compSearch.toLowerCase()));
  const compPages = Math.max(1, Math.ceil(filteredComps.length / PER));
  const cCur = Math.min(compPage, compPages - 1);
 
  return (
    <div style={{ padding: 'clamp(16px, 3vw, 36px)', paddingTop: 0, fontFamily: 'var(--font-body)' }}>
      <div style={{ marginTop: 'var(--space-xs)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-sm)', marginBottom: 'var(--space-6)' }}>
          <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 'var(--font-size-xl)', fontWeight: 700, color: 'var(--neutral-800)', margin: 'var(--space-none)' }}>Author Comp Claims</h2>
          <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--neutral-400)' }}>· {filteredComps.length}</span>
        </div>
        <p style={{ fontSize: 'var(--font-size-sm)', color: '#8b7355', margin: '0 0 12px' }}>An author's "if you love X → read Y" claims by shared tags, before or regardless of the automatic pipeline created from them. Watch for comps riding traffic without genuinely matching, remove if so.</p>
        <input value={compSearch} onChange={e => { setCompSearch(e.target.value); setCompPage(0); }} placeholder="Search comp claims..."
          style={{ width: '100%', maxWidth: 420, padding: '8px 13px', border: '1px solid var(--brand-sand)', borderRadius: 'var(--radius-10)', fontSize: 'var(--font-size-sm)', outline: 'none', marginBottom: 'var(--space-md)', fontFamily: 'var(--font-body)' }} />
        <div style={{ border: '1px solid #ece3d4', borderRadius: 14, background: '#fdfbf7', padding: 'var(--space-14)' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-sm)', minHeight: 200 }}>
            {loading && <div style={{ padding: '30px', textAlign: 'center', color: 'var(--neutral-400)', fontSize: 'var(--font-size-sm)' }}>Loading...</div>}
            {!loading && filteredComps.length === 0 && <div style={{ padding: '30px', textAlign: 'center', color: 'var(--neutral-400)', fontSize: 'var(--font-size-sm)' }}>{comps.length === 0 ? 'No comp claims yet.' : 'No comps match.'}</div>}
            {filteredComps.slice(cCur * PER, cCur * PER + PER).map(x => (
              <div key={x.id} style={{ background: 'var(--brand-white)', borderRadius: 'var(--radius-lg)', border: '1px solid #ece3d4', padding: '13px 16px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 9 }}>
                  <div>
                    <p style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--neutral-800)', margin: '0 0 1px' }}>
                      {x.book_title} <span style={{ fontSize: 10.5, fontWeight: 400, color: 'var(--neutral-400)' }}>by {x.author_name}</span>
                    </p>
                    <p style={{ fontSize: 11.5, color: 'var(--neutral-600)', margin: 'var(--space-none)' }}>
                      comped to <strong style={{ color: 'var(--brand-rust)' }}>{x.comp_title}</strong>
                    </p>
                  </div>
                  <button onClick={() => removeComp(x)} style={{ padding: '5px 12px', background: 'var(--brand-white)', color: 'var(--feedback-error)', border: '1px solid #fecaca', borderRadius: 'var(--radius-md)', fontSize: 10.5, fontWeight: 600, cursor: 'pointer' }}>Remove</button>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-6)', flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 'var(--font-size-tiny)', fontWeight: 700, color: 'var(--neutral-400)', letterSpacing: 0.5 }}>SHARED TAGS:</span>
                  {(x.shared_tags || []).length > 0
                    ? (x.shared_tags || []).map((t, i) => (
                        <span key={i} style={{ fontSize: 10.5, padding: '3px 10px', borderRadius: 'var(--radius-lg)', background: 'var(--feedback-warning-bg)', color: 'var(--feedback-warning-ink)', border: '1px solid var(--feedback-warning-border)' }}>{typeof t === 'string' ? t : t.name || t}</span>
                      ))
                    : <span style={{ fontSize: 10.5, color: 'var(--neutral-400)', fontStyle: 'italic' }}>no shared tags</span>
                  }
                </div>
              </div>
            ))}
          </div>
          {filteredComps.length > PER && (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 'var(--space-md)', paddingTop: 'var(--space-md)', borderTop: '1px solid #ece3d4' }}>
              <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--neutral-400)' }}>Showing {cCur * PER + 1}–{Math.min(cCur * PER + PER, filteredComps.length)} of {filteredComps.length}</span>
              <div style={{ display: 'flex', gap: 'var(--space-6)', alignItems: 'center' }}>
                <button onClick={() => setCompPage(Math.max(0, cCur - 1))} disabled={cCur === 0} style={{ padding: '5px 12px', borderRadius: 'var(--radius-md)', border: '1px solid var(--brand-sand)', background: 'var(--brand-white)', color: cCur === 0 ? 'var(--neutral-300)' : 'var(--neutral-600)', fontSize: 'var(--font-size-xs)', fontWeight: 600, cursor: cCur === 0 ? 'default' : 'pointer' }}>← Prev</button>
                <span style={{ fontSize: 'var(--font-size-xs)', color: '#8b7355' }}>{cCur + 1}/{compPages}</span>
                <button onClick={() => setCompPage(Math.min(compPages - 1, cCur + 1))} disabled={cCur >= compPages - 1} style={{ padding: '5px 12px', borderRadius: 'var(--radius-md)', border: '1px solid var(--brand-sand)', background: 'var(--brand-white)', color: cCur >= compPages - 1 ? 'var(--neutral-300)' : 'var(--neutral-600)', fontSize: 'var(--font-size-xs)', fontWeight: 600, cursor: cCur >= compPages - 1 ? 'default' : 'pointer' }}>Next →</button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}